import { z } from "zod";
import { FACT_SOURCES } from "./athlete";
import { REASON_CODES } from "./engine-reasons";

/**
 * THE SUGGESTIONS DECK, ON THE WIRE.
 *
 * THIS FILE MIRRORS NO TABLE, AND THAT IS THE POINT. `engine_offer_records` stores an offer
 * as four provenance columns plus an opaque `record jsonb` that a fitting job reads WHOLE and
 * never queries inside — the deck is not served out of it. What this file mirrors is the
 * engine's published surface (`api/src/engine/contracts/index.ts`: `Offer`, `Session`,
 * `Refusal`, `SessionBlock`, `MovementLine`, `Reason`) as a TRANSPORT FORMAT, which is a
 * different thing from that surface.
 *
 * THE ENGINE'S TYPES ARE NOT RE-EXPORTED ACROSS THE NETWORK, deliberately. `api/src/engine/`
 * lives inside the service precisely so the app cannot see it: an athlete's client must never
 * re-rank or re-filter a suggestion, and being unable to import the engine is the cheapest
 * possible enforcement of that. Re-exporting its types here would undo the whole placement
 * decision and couple every screen to a shape the engine is entitled to change.
 *
 * SO THE SHAPES BELOW ARE DECLARED, NOT DERIVED — and they are deliberately NARROWER than the
 * engine's. Branded numbers arrive as numbers; `readonly` is a compile-time idea with no wire
 * form; the engine's open string aliases stay open here for the same reason they are open
 * there (`MovementId`, `EquipmentId`, `ShapeId` and friends name registries a coach may
 * extend without a deploy, so a closed enum here would delete content the service is happy to
 * serve).
 */

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE REASON CODES

   RE-EXPORTED, NEVER RE-DECLARED. They live in `./engine-reasons`, which exists as a second
   declaration of a list the engine owns — the app cannot import the engine and the engine
   cannot import `shared/`, so the list is declared twice and asserted equal in both
   directions by `api/tests/engine/engine-contract.int.spec.ts`. That test is pinned to that
   file by path; moving or copying the list breaks it.
   ══════════════════════════════════════════════════════════════════════════════════════ */

export { REASON_CODES, type ReasonCode } from "./engine-reasons";

/**
 * WHY A NUMBER IS THE NUMBER IT IS: the code identifies the rule, `inputs` are the values it
 * fired on, `params` name the tuning dials it read.
 *
 * NEVER A SENTENCE, WHICH IS THE ENTIRE DESIGN. The service does not format prose; it sends
 * the code and the values, and the app words it once per language. The wording lives in
 * `src/app/locales/<lang>/common.json` under `reasons.*`, keyed by the CODE itself — the
 * codes are dotted, i18next splits keys on dots, so a code's own structure is the nesting and
 * nothing has to transform anything at render time.
 *
 * `params` IS `string[]` AND NOT A CLOSED UNION for the same reason it is one in the engine:
 * the dial registry is not something either side of this boundary can name.
 */
export const reasonSchema = z.object({
  code: z.enum(REASON_CODES),
  inputs: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])),
  params: z.array(z.string()),
});

export type ReasonDTO = z.infer<typeof reasonSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE GEARS
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ALL THREE COLUMNS TRAVEL, HARDEST FIRST, EVEN THOUGH THE DESIGN SHOWS TWO.
 *
 * The deck renders RX (`push`) and Scaled (`mid`) and has no control for `easy`. It still
 * ships, because dropping a column AT THE CONTRACT is irreversible — re-adding it later is a
 * schema change, a service change and a release — while dropping it AT RENDER TIME is a
 * one-line choice the app owns. The contract carries what the engine produced; the screen
 * decides what a person sees.
 *
 * CLOSED, unlike the engine's `GearId`, which is an open alias so a module that imports
 * nothing can still be keyed by one. Here the set is genuinely closed already:
 * `engine_completion_records.gear` carries `check (gear in ('easy','mid','push'))`, so a
 * fourth intensity is a migration whatever this file says. The order is the gear registry's
 * own `order`, sorted hardest first — the order a card reads in.
 */
export const GEAR_IDS = ["push", "mid", "easy"] as const;
export const gearIdSchema = z.enum(GEAR_IDS);
export type GearId = (typeof GEAR_IDS)[number];

/**
 * WHICH "NO" THE CALLER WAS WILLING TO HAVE OVERRIDDEN. A genuinely closed set in the engine
 * (`RELAXATIONS`), so it is closed here — and declared ONCE, because it is named in two
 * places: on a session, as what was in force while it was built, and on the offer, as what
 * actually fired.
 */
export const RELAXATION_IDS = [
  "allow-over-the-time-band",
  "drop-coach-exclusions",
  "ignore-minutes-available",
] as const;
export const relaxationIdSchema = z.enum(RELAXATION_IDS);
export type RelaxationId = (typeof RELAXATION_IDS)[number];

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE FORMAT, AND THE FACTS THE TIMER BRANCHES ON
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * THE EIGHT THE LIBRARY DECLARES (BUD-366 adds `interval` and `max-unbroken`). `logs.ts`
 * imports this rather than restating it.
 */
export const METCON_FORMAT_IDS = [
  "for-time",
  "amrap",
  "emom",
  "max-reps",
  "tabata",
  "death-by",
  "interval",
  "max-unbroken",
] as const;
export const metconFormatIdSchema = z.enum(METCON_FORMAT_IDS);
export type MetconFormatId = (typeof METCON_FORMAT_IDS)[number];

/**
 * WHO HOLDS THE THROTTLE. `athlete` means they choose the pace and the clock only bounds it;
 * `structure` means the format sets it and there is nothing to choose.
 */
export const PACED_BY = ["athlete", "structure"] as const;
export const pacedBySchema = z.enum(PACED_BY);
export type PacedBy = (typeof PACED_BY)[number];

/**
 * WHAT THE PIECE IS SCORED BY — AS CODES, NOT AS THE REGISTRY'S SENTENCES.
 *
 * The format registry spells these as prose ("total time", "rounds + reps"). Prose cannot go
 * on this wire: the app would either print English into a German screen, or key a translation
 * off a sentence — and the engine's own registry carries the scar tissue from exactly that
 * mistake. Its `roundsFromClock` field exists because a rule once tested "whether the
 * format's human-facing `clock` sentence began with the words 'the dose'", and "a rule keyed
 * to the first two words of a sentence breaks when somebody improves the sentence".
 *
 * So these are identifiers, one per registry row, and the service's view is what maps the
 * registry's prose onto them.
 */
export const SCORED_BY = [
  "total-time",
  "rounds-plus-reps",
  "completion",
  "total-reps",
  "lowest-round",
  "minute-reached",
  /** BUD-366's `max-unbroken` — the fourth scoring axis: the size of the best of N attempts. */
  "best-unbroken-set",
] as const;
export const scoredBySchema = z.enum(SCORED_BY);
export type ScoredBy = (typeof SCORED_BY)[number];

/**
 * EACH SUGGESTION CARRIES ITS OWN FORMAT FACTS. THIS IS WHAT UNBLOCKS THE TIMER.
 *
 * The timer has to branch on how a piece is paced — a countdown for a fixed clock, a count-up
 * for a piece the athlete's own work ends, an interval beep where there is an interval. The
 * registry that knows all of that lives INSIDE the engine, which the app cannot see.
 *
 * THE WHOLE REGISTRY IS DELIBERATELY NOT PUBLISHED. The app renders one session's facts and
 * never enumerates the six formats; publishing the registry would be a second declaration to
 * keep in sync forever for something only ever read one row at a time. The reason codes earn
 * that treatment because the app must hold wording for codes it never receives — a format
 * fact is the opposite case, and treating them alike would be cargo cult.
 *
 * ⚠️ `max-reps` IS STRUCTURE-PACED AND ENDS AT FAILURE. Elsewhere it has been described as
 * self-paced; the registry says `paced: 'structure'`, `endsAtFailure: true`, and the code
 * wins. What is modelled here is what `api/src/engine/library/formats.ts` declares.
 *
 * WHAT IS OMITTED, AND WHY: `minMovements` and `restCanBeAdded` are DRAW-time constraints —
 * they decide what the engine may build, and nothing about a piece that already exists.
 * `titleWord`, `clock` and `note` are prose the engine composes its own `title` from, and the
 * title is already on the suggestion.
 */
export const formatFactsSchema = z.object({
  id: metconFormatIdSchema,
  paced: pacedBySchema,
  scoredBy: scoredBySchema,
  /**
   * The window one round runs in, where the format has one — sixty seconds for an EMOM,
   * twenty for a tabata. Null everywhere else.
   */
  intervalSec: z.number().int().positive().nullable(),
  /** True when the piece ends when the athlete fails. A max effort, and never an easy day. */
  endsAtFailure: z.boolean(),
  /** True when the format's own clock fixes the duration and no cap is written onto it. */
  durationIsFixed: z.boolean(),
  /**
   * TRUE WHEN THE CLOCK DECIDES HOW MANY ROUNDS HAPPEN, rather than the shape. Carried
   * ALONGSIDE `durationIsFixed` and not derived from it, because the two genuinely differ: a
   * max-reps piece runs the rounds it was written with even though its duration is also
   * fixed. Telling them apart is the timer's job and it needs both to do it.
   */
  roundsFromClock: z.boolean(),
});

export type FormatFactsDTO = z.infer<typeof formatFactsSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   ONE LINE OF ONE BLOCK
   ══════════════════════════════════════════════════════════════════════════════════════ */

/** The six blocks, in the order a session runs them. Never reordered, never repeated. */
export const BLOCK_IDS = [
  "warmup",
  "ramp",
  "strength",
  "conditioning",
  "accessory",
  "cooldown",
] as const;
export const blockIdSchema = z.enum(BLOCK_IDS);
export type BlockId = (typeof BLOCK_IDS)[number];

/** The standard a movement is performed to, RX and scaled, where one exists. */
export const movementStandardSchema = z.object({
  rx: z.string(),
  scaled: z.string(),
});

/**
 * "THIS IS WORTH MEASURING" — emitted by the engine alongside a load it had to reach down the
 * fallback ladder for. Nullable on the arms where the engine makes it optional; on `byFeel`
 * it is REQUIRED upstream, because a by-feel prescription is precisely the case where nothing
 * has ever been measured and saying so is the whole prescription.
 */
export const retestIntentSchema = z.object({
  movement: z.string(),
  what: z.enum(["max", "unbroken"]),
  /**
   * Never measured at all; measured long enough ago that the value has drifted; or measured,
   * fresh, and contradicted by the training itself — the working weight has drifted as far from
   * what the stored max implies as it is allowed to, so the number is wrong rather than old
   * (BUD-379).
   */
  because: z.enum(["never-measured", "stale", "disagrees-with-training"]),
});

export type RetestIntentDTO = z.infer<typeof retestIntentSchema>;

/**
 * WHAT THIS MOVEMENT IS LOADED FROM, flattened to a discriminated union of four answers and
 * NO NULL — the absence of a null is the point of the type. "Nothing to load" and "we could
 * not work out a load" are different facts, and were the same `null` until the engine split
 * them; every caller had collapsed both into "drop the movement".
 *
 * The engine's `Fact<number>` is flattened into ALL THREE of its parts — value, source and
 * `observedAt` — exactly as `athlete.ts` flattens the body-mass fact into three columns.
 * Carrying two of the three would drop the date a number was observed, which is the field
 * every ageing rule in `capability` measures against and the only thing separating a max
 * taken this month from one taken two years ago. `observedAt` is nullable because the engine
 * makes it optional: it is the honest state for anything derived from an undated input or
 * assumed outright. The confidence `Band` is flattened the same way, into `bandLow`/`bandHigh`.
 */
export const loadBasisSchema = z.discriminatedUnion("kind", [
  /** The athlete's own body is the load. There is nothing to put on it. */
  z.object({ kind: z.literal("unloaded"), movement: z.string() }),
  /** A share of a one-rep max — recorded, derived through the chain, or assumed. */
  z.object({
    kind: z.literal("max"),
    movement: z.string(),
    /** Which rung of the fallback ladder answered. `by-feel` is its own arm below. */
    /* 🚨 `estimated` IS THE RUNG A LOGGED SET ANSWERS — and leaving it off this enum turned
       every session carrying one into a 500. The engine priced the bar correctly, the view
       assembled correctly, and the CONTRACT PARSE on the way out refused a value the engine had
       just published. `sendOk` accepts only the branded return of `view()`, so a body that does
       not match this line does not reach the athlete at all: the whole session fails, not the
       field. Any new rung has to arrive here in the same commit as the engine's own union. */
    rung: z.enum(["recorded", "estimated", "derived", "body-mass"]),
    maxKg: z.number(),
    maxSource: z.enum(FACT_SOURCES),
    /** `YYYY-MM-DD`. Null where nothing observed it — derived from an undated input, or assumed. */
    maxObservedAt: z.iso.date().nullable(),
    bandLow: z.number(),
    bandHigh: z.number(),
    confidence: z.number(),
    retest: retestIntentSchema.nullable(),
  }),
  /**
   * A DISCRETE OBJECT, which is not a percentage of anything. A kettlebell's weight does not
   * scale with a deadlift — a 210 kg puller still swings a 24 or a 32.
   */
  z.object({
    kind: z.literal("implement"),
    movement: z.string(),
    loadKg: z.number(),
    loadSource: z.enum(FACT_SOURCES),
    loadObservedAt: z.iso.date().nullable(),
    bandLow: z.number(),
    bandHigh: z.number(),
    confidence: z.number(),
    retest: retestIntentSchema.nullable(),
  }),
  /**
   * NO NUMBER AT ALL, AND SAYING SO IS THE PRESCRIPTION. A named RPE target plus an explicit
   * intent to measure — never a fabricated kilo figure wearing a percentage.
   */
  z.object({
    kind: z.literal("byFeel"),
    movement: z.string(),
    rpe: z.number(),
    confidence: z.number(),
    /** REQUIRED, not nullable: the engine guarantees one on every by-feel line. */
    retest: retestIntentSchema,
  }),
]);

export type LoadBasisDTO = z.infer<typeof loadBasisSchema>;

export const prescribedLoadSchema = z.object({
  /**
   * What the athlete actually picks up, in kg, as this room can build it. NULL when there is
   * no number to give — a by-feel rung, or a room that owns nothing that carries it.
   */
  kilos: z.number().nullable(),
  basis: loadBasisSchema,
  /** Share of the athlete's own one-rep max, where the load IS a share of one. */
  percentOfMax: z.number().nullable(),
  /**
   * HOW MANY IMPLEMENTS ARE IN THE HANDS. "@ 20 kg" on a dumbbell press and on a kettlebell
   * swing are different total loads.
   */
  handsOn: z.union([z.literal(1), z.literal(2)]),
  confidence: z.number(),
});

/**
 * A LINE THAT MEASURES RATHER THAN PRESCRIBES — a max set, or a max-effort round. NOT a
 * finisher: a finisher goes at the end when the athlete is cooked, which is the worst
 * possible moment to measure what they can string together.
 */
export const measuredSetSchema = z.object({
  style: z.enum(["timed", "unbroken"]),
  windowSec: z.number().int().positive().nullable(),
  /** The athlete's recorded number, when this re-tests one rather than establishing it. */
  retestOf: z.number().nullable(),
});

/**
 * THE PAIRING LABELS, CLOSED. `PairingLabel` in the engine's contracts is the same three, and
 * the union's LENGTH is the ceiling on how many lifts may share one set — said once, at the
 * type level, rather than as a number three modules check for themselves.
 */
export const pairingLabelSchema = z.enum(["A1", "A2", "A3"]);

export type PairingLabelDTO = z.infer<typeof pairingLabelSchema>;

export const movementLineSchema = z.object({
  movement: z.string(),
  /** The name a card prints. Carried rather than looked up: the engine publishes no catalog. */
  name: z.string(),
  modality: z.string(),
  /**
   * How many, in `unit`. NULL when the block's prescription carries the number instead — a
   * ladder prints one scheme for every movement in it, and a death-by prints none at all.
   */
  count: z.number().nullable(),
  /**
   * EVERYTHING OF THIS MOVEMENT IN THE WHOLE BLOCK — five rounds of twelve is a `count` of
   * twelve and a `total` of sixty. Both travel, because the printed line only ever carries
   * the per-round number and the totals are what a checklist and a history row need.
   */
  total: z.number().nullable(),
  unit: z.string(),
  /** How many of `count` fall on each side, for a movement that alternates. */
  perSide: z.number().nullable(),
  load: prescribedLoadSchema.nullable(),
  standard: movementStandardSchema.nullable(),
  /**
   * WHERE THE NUMBER IS MORE THAN THE ATHLETE CAN HOLD IN ONE GO, the sets to break it into.
   * You break a set; you do not shrink the workout.
   */
  breakInto: z.number().int().positive().nullable(),
  measured: measuredSetSchema.nullable(),
  sets: z.number().int().positive().nullable(),
  /**
   * WHICH LIMB OF A PAIRED SET THIS IS — `A1` / `A2` / `A3` — or null on a line that stands
   * on its own. It is what lets a card draw the bracket: two lifts printed as two rows is a
   * DIFFERENT session from two lifts printed as one alternating set, because the second has
   * no rest in the middle.
   *
   * 🚨 THIS SCHEMA IS A PLAIN `z.object` AND THEREFORE STRIPS WHAT IT DOES NOT DECLARE. A
   * field added to the engine's `MovementLine` and not added here vanishes between the
   * service and the app with nothing failing anywhere — no parse error, no type error, just
   * a value the client never sees. Every field on the engine's line has to be written twice
   * on purpose, and that is the reason.
   */
  pairing: pairingLabelSchema.nullable(),
  /**
   * A CAPPED-CHIPPER STATION'S OWN CLOCK, IN MINUTES — null on every other line. Not the same
   * fact as the block's whole-piece `cap`; see the engine's `MovementLine.stationCapMinutes`.
   */
  stationCapMinutes: z.number().positive().nullable(),
  /** The expected work in seconds, at this athlete's rate. Never zero. */
  seconds: z.number().positive(),
  equipment: z.array(z.string()),
  /** `none` · `wall` · `floor` · `outdoor` — the room this movement needs. */
  space: z.string(),
  body: z.array(z.string()),
});

export type MovementLineDTO = z.infer<typeof movementLineSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE BLOCK
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * THE PRESCRIPTION LINE, AS DATA RATHER THAN AS A SENTENCE. "21-15-9 reps for time" is a
 * RENDERING of these numbers, and the rendering belongs to whoever is drawing the card.
 */
export const prescriptionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ladder"), scheme: z.array(z.number()) }),
  z.object({ kind: z.literal("rounds"), rounds: z.number().int() }),
  z.object({ kind: z.literal("clock"), minutes: z.number(), alternating: z.boolean() }),
  z.object({ kind: z.literal("max-effort-rounds"), rounds: z.number().int() }),
  z.object({ kind: z.literal("one-pass") }),
  z.object({ kind: z.literal("until-failure") }),
  z.object({ kind: z.literal("sets"), sets: z.number().int(), reps: z.number().int().nullable() }),
  z.object({ kind: z.literal("supersets"), sets: z.number().int() }),
  z.object({ kind: z.literal("complex"), sets: z.number().int() }),
  z.object({ kind: z.literal("build"), sets: z.number().int(), topReps: z.number().int() }),
  z.object({ kind: z.literal("ramp"), steps: z.array(z.number()) }),
  z.object({ kind: z.literal("drills") }),
]);

export type PrescriptionDTO = z.infer<typeof prescriptionSchema>;

/**
 * WHERE THE REST GOES, AND WHO OWNS IT. Only a format whose clock runs through the rest may
 * have it written in: an AMRAP scores rounds, so prescribed rest would be counted as work.
 */
export const restRuleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({
    kind: z.literal("between-rounds"),
    seconds: z.number(),
    /** True when it is part of the piece rather than the athlete's own. */
    prescribed: z.boolean(),
  }),
  z.object({ kind: z.literal("remainder-of-interval") }),
  z.object({ kind: z.literal("as-needed") }),
  z.object({ kind: z.literal("clock-catches-you") }),
  z.object({ kind: z.literal("between-sets"), seconds: z.number() }),
  /**
   * NO REST INSIDE THE GROUP, `seconds` AFTER IT — a superset or a giant set. The group's SIZE
   * is not on the rule: the lines carry `pairing`, so whatever draws the sentence can count
   * them. It was `after-each-pair` until BUD-331, when a third lift made that name untrue.
   */
  z.object({ kind: z.literal("after-each-group"), seconds: z.number() }),
]);

export type RestRuleDTO = z.infer<typeof restRuleSchema>;

/**
 * THE CAP, AND WHAT KIND OF THING IT IS. Three different facts wore one number in the
 * prototype: "AMRAP 12:00, cap 16 min" is not a sentence a coach writes, and a death-by has
 * no cap at all because fatigue ends it far earlier than any calculation implies.
 */
export const capSchema = z.discriminatedUnion("kind", [
  /** A ceiling: the reps are the dose and this is the point past which you stop. */
  z.object({ kind: z.literal("ceiling"), minutes: z.number() }),
  /** The format's own clock fixes the duration; nothing is written on top of it. */
  z.object({ kind: z.literal("fixed"), minutes: z.number() }),
  /** Self-terminating — it ends when the athlete does. */
  z.object({ kind: z.literal("self-terminating") }),
]);

export type CapDTO = z.infer<typeof capSchema>;

export const sessionBlockSchema = z.object({
  id: blockIdSchema,
  /** How the reps are arranged. Open alias — `library` owns the union. */
  structure: z.string().nullable(),
  /** Which format this block runs, where it has one. The FACTS about it are on the suggestion. */
  format: z.string().nullable(),
  prescription: prescriptionSchema,
  lines: z.array(movementLineSchema),
  rest: restRuleSchema,
  cap: capSchema.nullable(),
  /** Why this block is dosed the way it is. Codes, never sentences. */
  reasons: z.array(reasonSchema),
  /** The block's expected work in seconds: the lines, plus fatigue, transitions and rest. */
  seconds: z.number(),
  /**
   * THE WINDOW ONE ROUND RUNS IN, on the value rather than looked up. The interval is a fact
   * about the block that SHIPPED, so the block carries it — reading it back off the format
   * registry would be reading the plan instead of the piece that was actually built.
   */
  intervalSec: z.number().int().positive().nullable(),
});

export type SessionBlockDTO = z.infer<typeof sessionBlockSchema>;

/**
 * WHAT THIS SESSION NEEDS, IN PILES — the equipment checklist. Derived from the blocks by the
 * engine, never scanned out of a rendered card.
 */
export const requirementGroupSchema = z.object({
  /** An equipment category, or `space` for what the room itself must provide. */
  category: z.string(),
  items: z.array(z.string()),
});

/**
 * HOW LONG, AND HOW SURE. `workMinutes` is the conditioning piece alone; `totalMinutes` is
 * every block added up. Built from EXPECTED WORK, never from the caps: a cap is the worst
 * case, and summing worst cases sold a twenty-nine minute session as forty-five.
 */
export const sessionEstimateSchema = z.object({
  totalMinutes: z.number(),
  workMinutes: z.number(),
  confidence: z.number(),
});

/**
 * WHAT A VERIFICATION GATE FOUND.
 *
 *   reject — this session must not ship as drawn. It reaches a reader only when the
 *            coordinator ran out of redraws and shipped the least-rejected candidate anyway.
 *   warn   — it may ship, and the output has to say so.
 *
 * A WARNING NOBODY SEES IS A COMMENT, which is why findings are on the wire at all.
 */
export const findingSchema = z.object({
  /** Which gate. A code, never a sentence — same rule as a reason's code. */
  gate: z.string(),
  severity: z.enum(["reject", "warn"]),
  scope: z.enum(["session", "offer", "receipt"]),
  reason: reasonSchema,
});

export type FindingDTO = z.infer<typeof findingSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   A COLUMN: ONE GEAR, EITHER A SESSION OR A REFUSAL
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * A LIVE COLUMN.
 *
 * `offered: true` IS A WIRE FIELD THE ENGINE DOES NOT HAVE, added deliberately. The engine
 * tells a session from a refusal by `kind: 'session'` against `offered: false` — two
 * different discriminants, which a client can only reconcile by testing which fields are
 * present. That is precisely the shape this codebase refuses: "a client that cannot ask has
 * to infer the shape from which fields are present, and a field that is merely optional reads
 * as a permission." One discriminant, asked once.
 */
export const offeredSessionSchema = z.object({
  offered: z.literal(true),
  gear: gearIdSchema,
  /** In block order, never repeated. A session may omit a block; it never reorders one. */
  blocks: z.array(sessionBlockSchema),
  requirements: z.array(requirementGroupSchema),
  estimate: sessionEstimateSchema,
  /**
   * THE ONE MACHINE THIS SESSION USES, or null. Two ergs in one session is a transition, not a
   * stimulus.
   */
  machine: z.string().nullable(),
  /**
   * WHAT THE GATES SAID. Never absent: there is no path that builds a session without the
   * registry running over it, so an empty array means "every gate passed" and never "nobody
   * asked".
   */
  findings: z.array(findingSchema),
  /** The relaxations that were in force while this session was built and checked. */
  relaxations: z.array(relaxationIdSchema),
});

export type OfferedSessionDTO = z.infer<typeof offeredSessionSchema>;

/**
 * A DECLINED COLUMN, WHICH IS A RESULT AND NOT AN ERROR. "No dose of these movements fits
 * this interval" is better output than a bad session, and an offer with two live columns and
 * one refusal is a NORMAL SUCCESS.
 *
 * IT CARRIES A WHOLE REASON, NOT A BARE CODE. A refusal answers "why" to the same standard a
 * prescription does: the code, the values it fired on, and the dials it read. There is no
 * free-text `detail` field, because that is the door a sentence walks back in through.
 */
export const refusedColumnSchema = z.object({
  offered: z.literal(false),
  gear: gearIdSchema,
  reason: reasonSchema,
});

export type RefusedColumnDTO = z.infer<typeof refusedColumnSchema>;

export const sessionColumnSchema = z.discriminatedUnion("offered", [
  offeredSessionSchema,
  refusedColumnSchema,
]);

export type SessionColumnDTO = z.infer<typeof sessionColumnSchema>;

/**
 * ALL THREE COLUMNS, BY NAME, ON EVERY SUGGESTION.
 *
 * An explicit object rather than a record keyed by gear: the three keys are required, so a
 * column that failed to build is a REFUSAL and never a missing key. A client reading a record
 * has to handle "absent" as a fourth case that means nothing in particular.
 *
 * The toggle between RX and Scaled must not re-fetch — they are the same workout at two doses
 * — so both arrive together, and so does the third the screen does not draw.
 */
export const sessionColumnsSchema = z.object({
  push: sessionColumnSchema,
  mid: sessionColumnSchema,
  easy: sessionColumnSchema,
});

export type SessionColumnsDTO = z.infer<typeof sessionColumnsSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE RECEIPT, AND THE DRAW'S OWN RECORD
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHAT THE ENGINE DID WITH ONE DECLARED INPUT OF THE REQUEST.
 *
 *   used   — read, and it changed something.
 *   empty  — read, and there was nothing there. An honest answer, not a failure.
 *   unused — not reached at all. The most useful status of the three: the receipt saying out
 *            loud that an input the caller took the trouble to send made no difference.
 *
 * IT IS ON THE WIRE AND NOT OPTIONAL. Several published reason codes exist ONLY here —
 * `athlete.population-floor-assumed`, `room.floor-assumed`, `history.sourced-the-draw`,
 * `request.input-not-considered` and the restriction family — so an app that never received
 * the receipt would hold wording it could never render, and an athlete priced against a
 * population model would have nothing anywhere admitting it.
 */
export const consideredInputSchema = z.object({
  /** A dotted path into the request: `athlete.maxes`, `room`, `minutesAvailable`. */
  input: z.string(),
  status: z.enum(["used", "empty", "unused"]),
  /**
   * WHOLE REASONS RATHER THAN BARE CODES, which is the difference between "a restriction
   * applied" and "the restriction on your shoulders removed these eleven movements".
   */
  reasons: z.array(reasonSchema),
});

export type ConsideredInputDTO = z.infer<typeof consideredInputSchema>;

/** A relaxation the caller opted into that actually fired, with the reason naming what it rescued. */
export const relaxationSchema = z.object({
  id: relaxationIdSchema,
  reason: reasonSchema,
});

export type RelaxationDTO = z.infer<typeof relaxationSchema>;

/** Something the engine looked at and turned down. An identifier, never prose. */
export const rejectionSchema = z.object({
  what: z.string(),
  reason: reasonSchema,
});

/** A candidate the gates refused, and every reject that killed it. */
export const discardedDrawSchema = z.object({
  shape: z.string(),
  format: z.string(),
  findings: z.array(findingSchema),
});

/**
 * WHAT DID NOT MAKE IT INTO THE OFFER — the draw's own record.
 *
 * `rejected` is what the DRAW turned down: shapes the library holds that this room and this
 * athlete cannot fill, plus anything the coach banned. `discarded` is what the GATES turned
 * down. Both are the signal that shows where the shape library is thin, and a refusal nobody
 * counts is a content gap nobody finds.
 *
 * OPTIONAL ON A SUGGESTION, AND THE ONLY OPTIONAL FIELD ON ONE. This is diagnostic rather
 * than athlete-facing, and a deck of ten suggestions carrying every rejected shape is a
 * payload nothing on that screen reads. A "why did I get this?" view asks for it; the deck
 * does not. Five published codes — the `coach.*` family and
 * `library.shape-cannot-be-filled-in-this-room` — are reachable only through here, so the
 * wording for them exists ahead of the screen that renders it.
 */
export const suggestionDiagnosticsSchema = z.object({
  rejected: z.array(rejectionSchema),
  discarded: z.array(discardedDrawSchema),
});

export type SuggestionDiagnosticsDTO = z.infer<typeof suggestionDiagnosticsSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE SUGGESTION, AND THE PAGE OF THEM
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ONE ENTRY IN THE DECK: one offer, one workout, three doses of it.
 *
 * `requestId` IS DERIVED FROM THE REQUEST, NOT MINTED. Two identical requests carry the same
 * id, which is what makes a replay a replay and what joins what was offered to what was done
 * (`session_logs.request_id`). It is a string and not a `z.uuid()` for that reason: it is the
 * engine's function of the request, not a database key.
 */
export const suggestionSchema = z.object({
  requestId: z.string(),
  /**
   * WHAT TO CALL THIS WORKOUT — "deadlifts + kettlebell swings AMRAP". Composed by the engine
   * ONCE and at offer scope rather than per column: the columns are one workout at three
   * intensities, therefore one name, and a client that composed it would be one of several
   * clients composing it differently.
   *
   * NULL WHEN EVERY GEAR REFUSED — a refusal has no session, so it has no name. Null rather
   * than an empty string, so a renderer must handle the case instead of printing a blank.
   */
  title: z.string().nullable(),
  /**
   * THE LIFT THE STRENGTH BLOCK IS ABOUT — "deadlifts". Composed by the engine at offer scope,
   * beside the title and from the same reading, so the card's heading and its lift line cannot
   * disagree about which movement the session is.
   *
   * IT IS NOT "THE STRENGTH BLOCK'S FIRST LINE", AND A CLIENT MUST NOT TAKE IT THAT WAY. The
   * block OPENS with a max set when a re-test is scheduled, because a measurement wants a fresh
   * athlete — so the first line names a movement the session is not about, and reading position
   * put "single-unders" on a card titled "step-ups + barbell rows for time". The engine reads
   * the LINE instead: a max set carries no dose and records a result, and no other strength line
   * does both. That rule lives in `api/src/engine/assembly/title.ts` and nowhere else, which is
   * why this field exists rather than a second implementation of it in each client.
   *
   * NULL WHEN THERE IS NO LIFT TO NAME — no strength block, a block that is only a measurement,
   * or an offer where every gear refused.
   */
  lift: z.string().nullable(),
  /** The one shape all three columns run. Null when nothing was drawn. */
  shape: z.string().nullable(),
  /** The one format all three columns run, with the facts the timer branches on. */
  format: formatFactsSchema.nullable(),
  columns: sessionColumnsSchema,
  /** The receipt: every declared input of the request, and what became of it. */
  considered: z.array(consideredInputSchema),
  /** Which of the caller's opted-in relaxations actually fired. Empty is the normal case. */
  relaxations: z.array(relaxationSchema),
  /**
   * WHAT THE OFFER-SCOPE GATES FOUND — the rules that are about the three columns COMPARED
   * AGAINST EACH OTHER, and so belong to no single one of them: does the harder gear actually
   * ask for more, is the hardest column a real dose, has this exact session been given before.
   */
  findings: z.array(findingSchema),
  diagnostics: suggestionDiagnosticsSchema.optional(),
  /** What produced this, so an offer can be fitted against later. Four stamps, all required. */
  provenance: z.object({
    engine: z.string(),
    parameterSet: z.string(),
    catalog: z.string(),
    library: z.string(),
  }),
});

export type SuggestionDTO = z.infer<typeof suggestionSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE DECK CARD — THE SAME OFFER, PROJECTED DOWN TO WHAT A CARD DRAWS (BUD-305)

   ═══ WHY A SECOND SHAPE RATHER THAN A SMALLER `suggestionSchema` ═══

   A page of ten full suggestions measured 180,832 bytes — 176.6 kB — over the wire, and the
   card renders about fifteen strings out of it. The weight is not one field: 15.6 kB of every
   17.4 kB card is `columns`, which is THREE COMPLETE SESSIONS, six blocks each, every movement
   line carrying its load basis (the kilo figure, its source, the date it was observed, the
   band around it, a confidence and a retest intent), its movement standard, its equipment,
   the body parts it works and its expected seconds. The card prints a name, a number and a
   unit. `considered` — the receipt — is another 1.5 kB per card that nothing on that screen
   reads.

   `suggestionSchema` IS NOT NARROWED, and it is not going to be. The session screen, the
   debrief and the engine bench all build against the whole offer, and the receipt carries
   published reason codes that are reachable through nothing else. What changes is WHICH SHAPE
   THE DECK'S PAGE CARRIES: the list read answers cards, and `GET /v1/sessions/suggestions/
   :seed` answers ONE whole suggestion for the screen that needs it.

   ═══ EVERY FIELD BELOW IS SPELLED EXACTLY AS ITS FULL COUNTERPART ═══

   `movement`/`name`/`count`/`total`/`unit`/`sets`, `load.kilos`, `id`/`prescription`/`cap`,
   `gear`/`offered`/`estimate` — all the same names, the same types and the same nullability
   as `movementLineSchema`, `sessionBlockSchema` and `offeredSessionSchema`. That is not tidy
   naming, it is the mechanism: a full DTO is assignable to the corresponding deck DTO, so the
   app's converter reads BOTH through one set of functions and there is no second reading of a
   movement line to drift (which is precisely the failure BUD-313 was).
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ONE PRINTED LINE — "15 kettlebell swings — 16 kg".
 *
 * `load` is an OBJECT carrying one number rather than a bare `kilos`, for the assignability
 * rule above: `prescribedLoadSchema` has this shape plus five more fields, so a full line
 * satisfies this one unchanged. The rest of the load — the basis, the band, the provenance of
 * the max it was priced off — is what a "why this weight?" view asks for, and it is on the
 * whole suggestion where that view will read it.
 */
export const deckLineSchema = z.object({
  movement: z.string(),
  /**
   * `ModalityId` — the engine's own three. Narrow, but it earns its place on the SMALLER
   * payload for a reason the rest of this shape does not have: it is what an athlete's movement
   * history groups on, and history is built from the sets a session records. Derived instead,
   * it would have to be derived identically in two places for ever, and every cheap proxy is
   * wrong somewhere — "has a weight on it" files a weighted pull-up as weightlifting.
   */
  modality: z.string(),
  name: z.string(),
  count: z.number().nullable(),
  total: z.number().nullable(),
  unit: z.string(),
  /**
   * HOW MANY OF `count` FALL ON EACH SIDE, for a movement that alternates. Null for everything
   * two-legged and two-armed, which is most of the vocabulary.
   *
   * 🚨 IT IS ON THE CARD'S PAYLOAD TOO, small as that payload is, because "6 split squats" is
   * AMBIGUOUS AND THE CARD IS WHERE IT IS FIRST READ. Six each side is twelve reps and roughly
   * twice the work of six in total, and an athlete who guesses wrong has done a different
   * workout. The engine already decides this — `MovementLine.perSide`, and its own vocabulary
   * says the card should print it — and the fact was simply being dropped on the way out.
   */
  perSide: z.number().nullable(),
  sets: z.number().int().positive().nullable(),
  /**
   * WHICH LIMB OF A PAIRED SET THIS IS, and it clears the same bar `modality` and `perSide`
   * were made to clear before it went on this payload.
   *
   * A CARD WHOSE STRENGTH BLOCK CARRIES TWO OR THREE LINES IS UNREADABLE WITHOUT IT. "Deadlift
   * 4 × 4 / bent-over row 4 × 8" drawn as two rows is a different session from the same two
   * drawn as one alternating set — the second has no rest in the middle and costs a third less
   * time — and the card is where an athlete FIRST decides whether to take it. It cannot be
   * derived on the phone: the prescription says `supersets`, but which line is A1 and which is
   * A2 is the ORDER THE ENGINE DREW, and an order is not recoverable from a count.
   *
   * One short nullable string per line, null on every line outside a paired block.
   */
  pairing: pairingLabelSchema.nullable(),
  load: z.object({ kilos: z.number().nullable() }).nullable(),
  /**
   * A CAPPED-CHIPPER STATION'S OWN CLOCK, IN MINUTES — the same fact `movementLineSchema`
   * carries, on the card too: "a per-station cap is not the same fact as a whole-piece cap"
   * (BUD-367) holds on the glance-read card exactly as much as on the full session screen.
   */
  stationCapMinutes: z.number().positive().nullable(),
});

export type DeckLineDTO = z.infer<typeof deckLineSchema>;

/**
 * ONE BLOCK OF A CARD, AND THE CARD CARRIES TWO OF THE SIX.
 *
 * ⚠️ THE STRENGTH BLOCK AND THE CONDITIONING BLOCK, because those are the two the card draws —
 * one lift line and the piece with the clock on it. The warm-up, the ramp, the accessory work
 * and the cool-down are real blocks an athlete is meant to do, and they arrive on the SESSION
 * screen, which reads the whole suggestion. A card that listed six blocks would be a session
 * screen printed small.
 *
 * `prescription` and `cap` travel WHOLE. They are unions the app already exhausts once
 * (`shapeOf`), they are what says "5×3" and "AMRAP 12:00", and narrowing them to the two
 * numbers the card prints would be a second, lossy reading of a union whose whole point is
 * that it is read once.
 */
export const deckBlockSchema = z.object({
  id: blockIdSchema,
  prescription: prescriptionSchema,
  cap: capSchema.nullable(),
  lines: z.array(deckLineSchema),
});

export type DeckBlockDTO = z.infer<typeof deckBlockSchema>;

/**
 * ONE GEAR OF A CARD. The refused arm is `refusedColumnSchema` ITSELF and not a copy: a
 * refusal is already as small as it gets — a gear and a whole reason — and the card has to be
 * able to say which half of the toggle is unavailable and why.
 */
export const deckColumnSchema = z.discriminatedUnion("offered", [
  z.object({
    offered: z.literal(true),
    gear: gearIdSchema,
    blocks: z.array(deckBlockSchema),
    /** The engine's own estimate. Never a sum computed on a client — see `sessionEstimateSchema`. */
    estimate: sessionEstimateSchema,
  }),
  refusedColumnSchema,
]);

export type DeckColumnDTO = z.infer<typeof deckColumnSchema>;

/**
 * ONE CARD IN THE DECK.
 *
 * The four offer-scope fields the card reads, and no others: `requestId` (which is what
 * `POST /start` joins on), `title`, `lift` — the engine's own answer to which movement the
 * session is about, never re-derived by position — and the format's FACTS, which is what the
 * card's heading and the timer branch on.
 *
 * WHAT IS DELIBERATELY NOT HERE, all of it still on `suggestionSchema`: `considered` (the
 * receipt), `relaxations`, `findings`, `provenance`, `shape`, and `diagnostics`, which the
 * deck has never carried. Every one is diagnostic or archival rather than something a card
 * draws, and every one is a field the single-offer read still answers with.
 */
export const deckCardSchema = z.object({
  requestId: z.string(),
  title: z.string().nullable(),
  /**
   * THE SHAPE THIS CARD RUNS — `COUPLET-WG`, `LADDER-21` (BUD-356).
   *
   * ⚠️ IT IS PRINTED VERBATIM, and that is the design's decision rather than an accident of
   * having nothing better: frame `10c` shows the id on the card, and `shapes.ts` says the ids
   * are written to be read. `GET /v1/shapes` publishes the same string, so a card and the shape
   * picker name one thing one way.
   *
   * NULLABLE, because `Offer.shape` is: an offer where every column refused drew no shape, and
   * a card for one has nothing to name. The whole suggestion has carried this since BUD-278
   * (`suggestionSchema`); the CARD did not, so the redesigned deck had nothing to print.
   */
  shape: z.string().nullable(),
  lift: z.string().nullable(),
  format: formatFactsSchema.nullable(),
  columns: z.object({
    push: deckColumnSchema,
    mid: deckColumnSchema,
    easy: deckColumnSchema,
  }),
});

export type DeckCardDTO = z.infer<typeof deckCardSchema>;

/**
 * THE CURSOR IS THE ENGINE'S SEED, WHICH IS WHY IT IS A NUMBER.
 *
 * The deck has no ranker and no suggestions table: there is nothing stored to page THROUGH.
 * The engine is a pure function of a request, and the seed is what makes one request differ
 * from the next, so paging is counting seeds. A page of ten starting at seed N is seeds N
 * through N+9 and a next cursor of N+10.
 *
 * `z.coerce` because this validates a QUERY STRING as well as a body, and a query string has
 * no numbers in it — `?cursor=20` arrives as "20". Without it the same contract accepts a
 * payload and rejects the identical request as a query.
 *
 * TEN, AND NEVER MORE THAN FIFTY. A suggestion is not a list row: it is three full sessions,
 * six blocks each, every line carrying its own load provenance. Ten is a deck somebody swipes
 * through; fifty is the bound that keeps one request from becoming the outage, and it is low
 * because each item is expensive to GENERATE and not merely to send — the service runs the
 * engine once per seed per gear.
 *
 * THERE IS NO SKIP CONTRACT. A skip means nothing here and posts nothing: with no ranker to
 * teach, a skip is the athlete moving to the next seed, which the cursor already says.
 */
export const suggestionsQuerySchema = z.object({
  cursor: z.coerce.number().int().min(0).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

export type SuggestionsQuery = z.infer<typeof suggestionsQuerySchema>;

/**
 * `nextCursor` IS NEVER NULL HERE, unlike every other page in this codebase. The seeds do not
 * run out — there is always another session to generate — so "the end of the list" is not a
 * state this deck can reach, and a nullable field would be a case no caller could ever hit
 * and every caller would have to write.
 *
 * ⚠️ ITS ITEMS ARE `deckCardSchema` AND NOT `suggestionSchema` (BUD-305). A page of ten whole
 * suggestions is 176.6 kB, measured, and a card draws about fifteen strings out of one. The
 * whole offer is still answered — one at a time, by `GET /v1/sessions/suggestions/:seed`, for
 * the session screen that actually prints all six blocks. Read the header above
 * `deckLineSchema` for why the card is a second shape rather than a narrowed first one.
 */
export const suggestionsPageSchema = z.object({
  items: z.array(deckCardSchema),
  nextCursor: z.number().int().min(0),
});

export type SuggestionsPageDTO = z.infer<typeof suggestionsPageSchema>;

/**
 * The seed in `GET /v1/sessions/suggestions/:seed` — ONE WHOLE SUGGESTION, asked for by name.
 *
 * ⚠️ THE PATH IS UNDER `/suggestions` AND NOT `/v1/sessions/:seed`, which is what an older
 * comment here described. `/v1/sessions/start` is a sibling, and a bare `:seed` at that level
 * is a parameter route sitting over a static one.
 */
export const suggestionSeedSchema = z.object({ seed: z.coerce.number().int().min(0) });

export type SuggestionSeedParam = z.infer<typeof suggestionSeedSchema>;

/**
 * COMMITTING TO ONE CARD — the body of `POST /v1/sessions/start`.
 *
 * ═══ THERE IS NO ID TO SEND, AND THAT IS THE DESIGN ═══
 *
 * Nothing is stored when an offer is drawn, so an offer is REPRODUCIBLE but not ADDRESSABLE:
 * there is no row to name and no identifier to pass. Identity is created by COMMITTING to an
 * offer rather than by being shown one — so this payload carries the SEED the athlete was
 * looking at, the service RE-DRAWS at that seed, and the id comes back on the response. Every
 * screen after the deck addresses that returned id.
 *
 * ⚠️ THE SESSION ITSELF IS NEVER SENT, AND MUST NEVER BECOME A FIELD HERE. A client that could
 * post a session could post one it was never offered — a workout at a load nothing prescribed,
 * with a stamp claiming the engine produced it, landing in the same evidence table a fitting
 * job reads in bulk. Re-drawing from a seed costs about two milliseconds and makes that
 * unrepresentable rather than merely refused.
 *
 * `gear` IS REQUIRED AND HAS NO DEFAULT. The deck shows RX and Scaled side by side and the
 * athlete picks one; a default here would file everybody who tapped Scaled as having trained
 * RX, which is a fact about their body that nothing downstream could ever correct. It is the
 * whole `GearId` set rather than the two the deck draws, because the contract carries what the
 * engine produced and the screen decides what a person sees.
 *
 * DECLARED HERE RATHER THAN COMPOSED IN THE SERVICE (BUD-278). It shipped for one review round
 * as `suggestionSeedSchema.extend({ gear: gearIdSchema })` inside the domain's `routes.ts`,
 * which type-checks and validates perfectly — and leaves the APP with no schema to reject a bad
 * body against, so it pays for the round trip to be told. One definition of "valid" serving
 * both halves is the entire reason this directory exists.
 */
export const startSessionSchema = z.object({
  seed: z.coerce.number().int().min(0),
  gear: gearIdSchema,
});

export type StartSessionPayload = z.infer<typeof startSessionSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════════
   WHICH SHAPES FIT TODAY, AND WHY THE REST DO NOT (BUD-333)

   `GET /v1/shapes` publishes the library — the same thirty rows for everybody, cacheable,
   static per deploy. This is the OTHER half of the shape picker (`10b`): the summary line
   `3 of 30 fit today · why?` and the breakdown it expands into, which is a different answer
   for every athlete because it is about their gym and their wishes.

   IT LIVES UNDER `sessions` AND NOT UNDER `shapes` DELIBERATELY. `shapes` answers what the
   library HOLDS and its own header says per-athlete fit is out of scope; this answers what
   could be DRAWN, which is the draw's question asked without drawing. The service resolves it
   against exactly the context a suggestion is drawn against — one athlete, one room, one set
   of dials, one content overlay — and resolving that twice, in two domains, is two answers to
   one question that come apart the first time either moves.
   ══════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * THE ATHLETE'S MOVEMENT WISHES, ON THE QUERY.
 *
 * COMMA-SEPARATED AND NOT REPEATED KEYS, because this is the one shape every HTTP client, every
 * proxy and every cache key handles identically — `?avoid=burpee,thruster` is one string, and
 * a phone on a gym's wifi retries it byte for byte. Repeated `?avoid=` params are legal and are
 * parsed differently by enough middleware to be worth not relying on.
 *
 * ⚠️ SENT BY THE CLIENT RATHER THAN READ FROM STORAGE, TODAY. The product decision is that
 * wishes are durable athlete state read on every draw (`docs/design-handoff/`, Decided #2), and
 * BUD-334 is the issue that gives them a home. Until then the picker holds them and sends them,
 * which is what the screen needs anyway: the athlete toggles a wish and the counts move without
 * a write. When the durable store lands, an absent parameter means "use what is stored" and a
 * present one stays the live override — an additive change to this schema and to nothing else.
 *
 * BOTH DEFAULT TO EMPTY. No wishes is the normal request, and it must not be a special case.
 */
export const shapeFitQuerySchema = z.object({
  /**
   * MOVEMENTS THE ATHLETE WANTS TODAY. A shape qualifies when SOME legal filling of it can hold
   * at least one of them — any, not all: three wants over a two-slot shape would otherwise make
   * every shape unavailable, and the filter is meant to steer rather than to empty the deck.
   */
  want: z
    .string()
    .transform((raw) => raw.split(",").map((one) => one.trim()).filter(Boolean))
    .pipe(z.array(z.string()).max(20))
    .default([]),
  /** Movements the athlete does not want today. Hard: a shape that can only be filled with one
   * of these does not fit. */
  avoid: z
    .string()
    .transform((raw) => raw.split(",").map((one) => one.trim()).filter(Boolean))
    .pipe(z.array(z.string()).max(20))
    .default([]),
});

export type ShapeFitQuery = z.infer<typeof shapeFitQuerySchema>;

/**
 * WHO A CAUSE BELONGS TO — which is what decides the control the client draws next to it.
 *
 * 🔑 THE ONE DISTINCTION THIS SCREEN IS BUILT ON. A wish is droppable and a gym limit is not,
 * and the design is explicit that the two must never share a control or a badge style. Sent as
 * a kind rather than inferred from the code, so a client switches on three values instead of on
 * a list of codes it has to keep in step with the engine.
 *
 *   wish  — the athlete asked for this and can take it back here. The only kind that carries a
 *           drop control, and the only kind whose count is an exact promise.
 *   gym   — a fact about the room. Worth saying; nothing on this screen changes it.
 *   fixed — neither: a declared restriction, what this athlete can do today, or a gap in the
 *           library. No control, because there is none to offer.
 *
 * A CLOSED ENUM, unlike most ids on this wire. The three are not content a coach extends —
 * each one means a different SCREEN, so a fourth would need a client that knows what to draw
 * for it, and shipping one to a build that does not is a blank row.
 */
export const fitCauseKindSchema = z.enum(["wish", "gym", "fixed"]);

export type FitCauseKindDTO = z.infer<typeof fitCauseKindSchema>;

/**
 * ONE REASON SHAPES ARE UNAVAILABLE, AND EXACTLY WHICH ONES.
 *
 * `shapes` AND `count` BOTH, WHICH IS DELIBERATE REDUNDANCY. The summary line prints a number
 * and the expanded row greys named rows, so both are needed — and publishing only the list
 * would put `list.length` on the client, which is the arithmetic BUD-333 exists to keep on the
 * service.
 */
export const shapeFitCauseSchema = z.object({
  kind: fitCauseKindSchema,
  /** A code and the values it fired on. Worded by the app, off `reasons.<code>`. */
  reason: reasonSchema,
  /** The shapes attributed to this cause, in library order. */
  shapes: z.array(z.string()),
  /** `shapes.length`. Sent so no caller computes it. */
  count: z.number().int().min(0),
});

export type ShapeFitCauseDTO = z.infer<typeof shapeFitCauseSchema>;

/**
 * WHICH SHAPES FIT TODAY, AND WHY THE REST DO NOT.
 *
 * 🔑 THE CAUSES PARTITION THE BLOCKED SHAPES: `fitting.length + sum(causes.count) === total`,
 * always. Every blocked shape is attributed to exactly one cause, so the breakdown adds up to
 * the summary line. The obvious implementation does not — a shape blocked by a missing rower
 * AND by "no burpees" is counted by both, and a thirty-shape library then explains forty.
 * `api/src/engine/assembly/fit.ts` says how the attribution is decided; the short version is
 * that causes are tried softest first, so a shape blocked by a wish AND the gym is attributed
 * to the GYM and the wish's count never over-promises.
 *
 * ⚠️ `total` IS THE SERVICE'S COUNT OF ITS OWN LIBRARY, not a constant. It is thirty as
 * supplied and the shape library is content a coach may extend, so a client that hardcoded 30
 * would print "3 of 30" over a library of thirty-four.
 */
export const shapeFitSchema = z.object({
  /**
   * WHICH LIBRARY THIS IS — the same stamp `GET /v1/shapes` sends as `libraryVersion`, so a
   * client can tell whether this fit is about the catalogue it is currently drawing.
   */
  libraryVersion: z.string(),
  /** Every shape considered. */
  total: z.number().int().min(0),
  /** The ones that could be drawn today, in library order. */
  fitting: z.array(z.string()),
  /** The ones that could not. */
  blocked: z.array(z.string()),
  /** Why. Biggest first. Empty exactly when nothing is blocked. */
  causes: z.array(shapeFitCauseSchema),
});

export type ShapeFitDTO = z.infer<typeof shapeFitSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   WORK ANSWERED ON DEMAND, FOR A METCON ALREADY DRAWN (BUD-335)

   A drawn session is metcon-only (BUD-330). Strength and accessory are EMPTY SLOTS the
   athlete opens afterwards, and this is what comes back when they do: three offers, each
   with a truthful `+minutes` — which is the whole reason the call is worth making rather
   than the client picking a lift off a list. The engine already knows what the metcon works,
   what the warm-up covered, and what the bar has to climb to.

   ═══ IT IS ADDRESSED BY SEED, LIKE EVERYTHING ELSE ABOUT AN OFFER ═══

   Nothing is stored when a session is drawn, so there is no offer row to hang this off. The
   seed IS the session: `GET /v1/sessions/suggestions/:seed/work` re-draws at that seed —
   the same draw, including the redraws the gates forced — and composes around the metcon
   that came out. Two calls at one seed therefore answer the same three offers, for ever,
   with nothing stored and nothing to invalidate.

   ═══ WHAT IS DELIBERATELY NOT HERE ═══

   ADDING one. The athlete tapping "Add deadlift" has to write the chosen block somewhere
   durable, and the session's own row holds a seed and a gear rather than blocks. That is a
   schema change and a separate issue; this action ANSWERS offers and writes nothing.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHICH EMPTY SLOT WAS OPENED.
 *
 *   strength   — the slot ABOVE the metcon: a lift, its ramp and its working sets.
 *   accessory  — the slot BELOW it: light, non-barbell work aimed at what the session did
 *                not load.
 *
 * They are two different questions and not one question with a size parameter: a strength
 * offer is priced off a percentage of a one-rep max and needs a bar to climb, an accessory
 * offer is a set of drills sized to a number of minutes.
 */
export const WORK_SLOTS = ["strength", "accessory"] as const;
export const workSlotSchema = z.enum(WORK_SLOTS);
export type WorkSlot = (typeof WORK_SLOTS)[number];

/**
 * WHAT THE CALLER ASKS FOR — the gear they committed to, and whether the clock has run.
 *
 * `gear` IS REQUIRED AND HAS NO DEFAULT, for the same reason `startSessionSchema`'s is: an
 * offer is priced at the column the athlete actually took, and defaulting it would price
 * somebody's added work at an intensity they did not choose.
 *
 * 🚨 `metconStarted` UNLOCKS NOTHING AND ONLY EVER LOCKS. Work appended once the conditioning
 * piece has begun may not be heavy, may not be a build and may not carry a max set — a
 * measurement is never a finisher, and a heavy triple taken while cooked both risks the
 * athlete and corrupts the number it records. So `true` is the RESTRICTIVE value and `false`
 * is the default because the sheet's normal case is the slot opened before "Start metcon"
 * (design frame `3a`: "Runs before the metcon").
 *
 * NOT `z.coerce.boolean()`, which is a trap on a query string — `?metconStarted=false`
 * arrives as the non-empty string "false" and coerces to TRUE. See `listSessionLogsSchema`,
 * which met this first.
 */
export const workOffersQuerySchema = z.object({
  seed: z.coerce.number().int().min(0),
  slot: workSlotSchema,
  gear: gearIdSchema,
  metconStarted: z
    .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
    .default(false),
});

export type WorkOffersQuery = z.infer<typeof workOffersQuerySchema>;

/**
 * ONE OFFER — everything the sheet draws, and everything the session would gain by taking it.
 *
 * `blocks` IS WHOLE `sessionBlockSchema`, not a deck-sized projection, and that is the point:
 * these are the blocks that get APPENDED to the athlete's session, so a lighter second shape
 * here would be a second reading of a movement line to drift (which is precisely what BUD-313
 * was). There are at most three of them — the prep, the bar ramp and the work itself — where
 * a deck card carried three whole sessions.
 *
 * IN THE ORDER THEY WOULD RUN. A prep block before a ramp before the working sets; never
 * reordered, and a block that is not needed is simply absent rather than empty.
 */
export const workOfferSchema = z.object({
  /**
   * WHICH OFFER THIS IS, stable for a given (seed, slot, gear) — the movement ids of the work
   * itself, joined. NOT a minted id: nothing is stored, so there is no row to name, and the
   * client sends this back when adding one. Two calls at one seed answer the same three ids.
   */
  id: z.string(),
  slot: workSlotSchema,
  /**
   * THE MOVEMENT THIS OFFER IS ABOUT — "deadlifts" — which is what the sheet's primary button
   * names ("Add deadlift"). The FIRST lift of the work block and not a composed phrase: a
   * superset's two lines and a giant set's three are on `blocks`, each carrying its own
   * `pairing` label, and composing "deadlift + row" is the client's sentence to write. The
   * engine holds no sentences.
   *
   * Null for an accessory offer, which is a set of drills and is about no single movement.
   */
  lift: z.string().nullable(),
  /**
   * 🚨 WHAT THE SESSION GAINS IN MINUTES — the number the sheet prints as `+22 min`, and the
   * one field on this payload that is a promise rather than a description.
   *
   * IT IS THE SUM OF EVERY BLOCK ON THIS OFFER, which is what makes it truthful: the extra
   * warm-up the athlete's own warm-up did not cover, the bar's climb to the working weight,
   * and the working sets with their rest. An estimate that left the ramp out would be a lie
   * the athlete pays for by skipping the climb to save the minutes the card promised them.
   */
  addsMinutes: z.number().int().nonnegative(),
  blocks: z.array(sessionBlockSchema),
  /** What this offer would add to the session's equipment checklist. */
  requirements: z.array(requirementGroupSchema),
  /**
   * WHY THIS OFFER IS A GOOD ONE — "same bar the metcon already needs" — as CODES WITH VALUES
   * and never as a composed sentence. `src/app/locales/<lang>/common.json` turns a code into copy,
   * which is the same split every other reason in this contract obeys.
   */
  reasons: z.array(reasonSchema),
  /**
   * 🚨 WHAT THE GATES SAID ABOUT THE METCON *WITH THIS APPENDED* — not about the offer alone.
   *
   * Every `Session` the engine hands over has been through the session registry, and an offer
   * that shipped as a bare delta would have slipped every one of those rules: the volume
   * ceiling, the buildable-load check, the restriction check and the time band have all only
   * ever seen the metcon. So the combined session is built, gated, and thrown away — this is
   * what it said.
   *
   * AN OFFER A GATE REJECTED IS NOT IN THE LIST AT ALL, and the gate's own reason is on the
   * sheet's `reasons` instead. So an entry here is a WARNING about a session the athlete may
   * still choose, never a refusal they are being shown anyway.
   */
  findings: z.array(findingSchema),
});

export type WorkOfferDTO = z.infer<typeof workOfferSchema>;

/**
 * THE SHEET — up to three offers, and why the list is what it is.
 *
 * AN EMPTY LIST IS A RESULT AND NOT AN ERROR, exactly as a refused column is. The rested-lift
 * filter is hard: a metcon that already works the whole body legitimately leaves nothing safe
 * to add, and `reasons` is what says so — the sheet's own gesture for that answer is already
 * drawn ("swipe down = no strength").
 *
 * `reasons` here is LIST-SCOPE and separate from each offer's own: it carries why the pool was
 * narrowed, why the list is volume-only this week, and why it came back empty.
 */
export const workOffersSchema = z.object({
  slot: workSlotSchema,
  gear: gearIdSchema,
  offers: z.array(workOfferSchema),
  reasons: z.array(reasonSchema),
});

export type WorkOffersDTO = z.infer<typeof workOffersSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   CANCELLING, AND THE TWO THINGS THAT HAVE TO BE WRITTEN DOWN ABOUT IT (BUD-337)

   Until now there was no cancel. The ✕ on the session screen cleared what the PHONE
   remembered and navigated home; nothing was sent, and `session_logs.outcome` stayed
   `started` for ever. `abandoned` was already in `COMPLETION_OUTCOMES` (`./logs`) and nothing
   wrote it. So athletes accumulated invisible open sessions and the engine was never told its
   offer was refused.

   THE VOCABULARY IS HERE AND THE COLUMNS IT DESCRIBES ARE ON `session_logs`, exactly as
   `metconFormatIdSchema` is: `./logs` imports the session vocabulary from this file and never
   the other way round, so a cancel reason declared over there would close a cycle.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHY THE ATHLETE STOPPED — a GLOBAL fact about the session, never a per-movement one.
 *
 * Five reasons, and cancelling with none of them is a first-class answer: the design's own
 * `Skip · just cancel`. A required reason buys a value that is a guess, and a guess is worse
 * than the absence because nothing downstream can tell the two apart.
 *
 * 🚨 NOTHING IN THE ENGINE REACTS TO ANY OF THESE, AND THAT IS DELIBERATE RATHER THAN
 * UNFINISHED. The coaching review is emphatic that the obvious reaction is wrong for three of
 * the five, and each of those is its own piece of work:
 *
 *   too-heavy        evidence the ESTIMATED MAX is wrong, not that the athlete is weak.
 *                    Lightening the programme treats a bad number as a weak athlete and never
 *                    fixes the number.
 *   too-long         evidence the DURATION ESTIMATE is wrong. Fix the estimate, not the dose.
 *   too-intense      about the GEAR, and any step down has to decay back.
 *   hurt             a clinical event, handled by the restriction path
 *                    (`src/features/athlete/components/Restrictions.tsx`), never by a weight
 *                    adjustment.
 *   not-in-the-mood  changes NOTHING. A programme that gets easier every time somebody has a
 *                    bad day converges on nothing.
 *
 * A placeholder reaction is not a smaller version of the right one; it is the wrong one
 * shipped early. So this vocabulary is RECORDED and read by nothing.
 */
export const CANCEL_REASONS = [
  "too-heavy",
  "too-intense",
  "too-long",
  "hurt",
  "not-in-the-mood",
] as const;
export const cancelReasonSchema = z.enum(CANCEL_REASONS);
export type CancelReason = (typeof CANCEL_REASONS)[number];

/**
 * WHICH KIND OF CANCEL IT WAS, WHICH IS A DIFFERENT FACT FROM WHY.
 *
 * 🚨 STORE THE DISTINCTION OR EVERY FUTURE REACTION IS BUILT ON NOISE. Cancelling before the
 * clock starts and abandoning at minute nine are not the same event:
 *
 *   not-begun   No block was ever opened. This is an OPINION ABOUT A CARD — the athlete looked
 *               at what was offered and declined it. Nothing happened to their body.
 *   under-way   At least one block was opened. This is EVIDENCE ABOUT A SESSION — work was
 *               done, fatigue was accumulated, and the day was spent.
 *
 * IT IS ALREADY LOAD-BEARING AND NOT A COLUMN FOR LATER. `sessions/repo.ts`'s history query —
 * the one that feeds the draw's memory of what this body has already been asked for — counts
 * `under-way` and skips `not-begun`, because a card that was never begun is not evidence about
 * a body. Without the distinction, declining a card would teach the draw the athlete had
 * trained it.
 *
 * ⚠️ THE CLIENT IS THE ONLY THING THAT KNOWS. The session clock runs on the phone and the
 * service is never told it started, so this arrives claimed rather than derived — the same
 * terms every score on a debrief arrives under.
 */
export const CANCEL_STAGES = ["not-begun", "under-way"] as const;
export const cancelStageSchema = z.enum(CANCEL_STAGES);
export type CancelStage = (typeof CANCEL_STAGES)[number];

/* ══════════════════════════════════════════════════════════════════════════════════════
   MUTATING A SESSION THAT HAS ALREADY STARTED (BUD-337)

   Two things a started session could never do: be CANCELLED, and GROW A BLOCK.

   ═══ 🚨 THE INVARIANT THIS BREAKS, AND HOW IT IS PRESERVED ═══

   A session used to be reproducible from `(seed, gear)` alone. `POST /v1/sessions/start`
   RE-DRAWS at the seed the client sends rather than trusting a session object in the body,
   and that redraw is the whole reason a client-sent session is impossible: there is no field
   to put one in. A session that has GROWN A BLOCK is no longer that redraw.

   The resolution is a MODIFICATION SET applied on top of the redraw, and the modification
   is itself server-composed:

     1. The client may only add work the SERVICE ALREADY OFFERED IT.
        `POST /v1/sessions/:id/blocks` takes an `offerId` — not blocks. The service re-draws
        at the session's own seed, re-composes the work offers for that slot
        (`GET /v1/sessions/suggestions/:seed/work`, BUD-335), and takes the one that bears
        that id. So the same property survives intact: nothing a client sends becomes work an
        athlete is asked to do, and there is no field to put a session in.

     2. What is STORED is the engine's own composed blocks, not the id.
        Re-composing on every read would make an addition VANISH the day the athlete's maxes,
        gym or week moved enough for a gate to refuse it — history would quietly lose a block
        the athlete actually trained. So the blocks are written down at the moment they are
        taken, exactly as `engine_offer_records` writes down the whole offer at the moment it
        is served, and for the same reason: evidence that was not recorded cannot be recovered
        later.

   ⚠️ WHAT IT COSTS, SAID PLAINLY. A session read back is now a HYBRID: the drawn half is
   still a redraw and can drift (the schema has always said "a redraw is not a photograph"),
   while the added half is frozen. The alternative — freezing the whole session at `start` —
   is a bigger change that contradicts `db/schemas/tables/session_logs.sql`'s explicit design
   ("The workout is not copied here, and it must not be"), and it would put a stored copy of
   every session beside an engine that can already produce it. Reversing this decision later
   means writing a snapshot at `start` and leaving these rows in place; it does not mean
   unpicking anything.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ADDRESSING A STARTED SESSION — the id `POST /v1/sessions/start` returned, which is the
 * `session_logs` row's own id and the same id the timer and the debrief use.
 *
 * ⚠️ IT IS NOT THE SEED. Everything ABOUT AN OFFER is addressed by seed, because an offer is
 * reproducible rather than addressable. A STARTED session is the opposite: committing to an
 * offer is what creates identity, so a mutation of one addresses the row that identity lives
 * on. A seed cannot be used here — two athletes share seeds, and one athlete can start the
 * same seed on two different days.
 */
export const startedSessionIdSchema = z.object({ id: z.uuid() });

export type StartedSessionIdParam = z.infer<typeof startedSessionIdSchema>;

/**
 * WHAT CANCELLING SENDS — the kind of cancel, and the reason or its absence.
 *
 * `reason` IS NULLABLE AND DEFAULTS TO NULL, which is the design's `Skip · just cancel`. A
 * required reason buys a value that is a guess.
 *
 * `stage` IS REQUIRED AND HAS NO DEFAULT. Only the client knows whether the clock ran, and a
 * default would file every cancel as whichever of the two facts was cheaper to assume — which
 * is precisely the bias `cap_hit`'s three states exist to prevent, one table over.
 *
 * ⚠️ THE ID IS IN THE PATH AND NOT IN THIS SHAPE. `domain()` parses `input` from the BODY
 * ALONE on a POST, so a path param never reaches a handler through it; the route parses the
 * id with `startedSessionIdSchema` above, which is what the app validates against too.
 */
export const cancelSessionSchema = z.object({
  stage: cancelStageSchema,
  reason: cancelReasonSchema.nullable().default(null),
});

export type CancelSessionPayload = z.infer<typeof cancelSessionSchema>;

/**
 * WHAT ADDING A BLOCK SENDS — an OFFER ID, and the terms it was offered under.
 *
 * 🚨 THERE IS NO FIELD FOR A BLOCK, AND THAT IS THE WHOLE SECURITY ARGUMENT. See the header
 * above: the service re-composes the work offers at this session's seed and takes the one
 * bearing this id, so an athlete can only ever add work the service itself offered them.
 *
 * `slot`, `gear` AND `metconStarted` ARE THE TERMS THE OFFER WAS COMPOSED UNDER, and they are
 * sent rather than inferred because the offer sheet was composed under them: the same seed
 * answers different offers for a different slot, a different gear, or a clock that has already
 * run. Sending them back is what makes the re-composition reproduce the sheet the athlete was
 * actually looking at rather than a different one that happens to share an id.
 *
 * 🚨 `metconStarted` ONLY EVER LOCKS, WHICH MEANS `false` IS THE PERMISSIVE VALUE — so a client
 * that under-reported it would be offered work it is not entitled to.
 *
 * ⚠️ WHAT IS AND IS NOT CHECKED, SAID PLAINLY. The session clock runs on the phone and the
 * service is never told it started, so this arrives CLAIMED rather than derived. The half that
 * can be checked is: it is MONOTONIC. Once a session has taken an addition under a started
 * clock, a later request may not claim the clock has not started — the bell cannot be un-rung.
 * A client that under-reports on the FIRST addition of a session is caught by nothing, and
 * catching it would need the service to know when the metcon began, which nothing records.
 */
export const addSessionBlockSchema = z.object({
  slot: workSlotSchema,
  /**
   * The offer's own id, as `GET /v1/sessions/suggestions/:seed/work` answered it. It is the
   * work's movement ids joined (`strength:back-squat`) — derived from the content rather than
   * minted, so it is stable for a `(seed, slot, gear)` and carries no session in it.
   */
  offerId: z.string().min(1).max(200),
  gear: gearIdSchema,
  metconStarted: z.boolean().default(false),
});

export type AddSessionBlockPayload = z.infer<typeof addSessionBlockSchema>;

/**
 * TAKING ONE BACK OUT — the id in the path, and which of the two slots to empty.
 *
 * ═══ 🚨 WHY A REMOVE HAS TO EXIST AT ALL (BUD-346) ═══
 *
 * An addition is DURABLE and the add action refuses to replace one ("that slot is already
 * filled", deliberately: replacing work an athlete may already have started is a different
 * action). Without this, an athlete who taps `Add deadlift` and changes their mind BEFORE
 * starting is committed to a lift for the life of that session — and design frame `5b` draws
 * `Swap` and `Remove` on a filled block precisely because they are not.
 *
 * ⚠️ IT TAKES A SLOT AND NOT A ROW ID, which is what makes it idempotent. There is one addition
 * per slot (`session_blocks` is unique on `(session_log_id, slot)`), so "empty the strength
 * slot" names exactly one row or none — and a phone that retried over a bad connection asks the
 * same question twice and gets the same answer, rather than a 404 it has to interpret.
 *
 * ⚠️ THE SLOT IS THE ONLY THING IT NARROWS BY. A caller cannot ask to remove a block the ENGINE
 * drew: only rows in `session_blocks` are reachable, and the drawn half of a session lives
 * nowhere but the redraw.
 *
 * ⚠️ IT IS PARSED FROM THE PATH. `domain()` reads a DELETE's `input` from the query MERGED WITH
 * the path params, so both fields arrive from `/v1/sessions/:id/blocks/:slot`.
 */
export const removeSessionBlockSchema = z.object({
  id: z.uuid(),
  slot: workSlotSchema,
});

export type RemoveSessionBlockParams = z.infer<typeof removeSessionBlockSchema>;

/**
 * ONE ADDITION, AS PROVENANCE — how a block that is not in the draw came to be in the session.
 *
 * It is published rather than kept internal because it is the ONLY thing on the wire that
 * distinguishes a block the engine drew from a block the athlete asked for, and a reader that
 * cannot tell them apart cannot honestly answer "what was I given?" versus "what did I choose?".
 */
export const sessionAdditionSchema = z.object({
  slot: workSlotSchema,
  offerId: z.string(),
  /** The movement it was about; null for an accessory list, which is about none. */
  lift: z.string().nullable(),
  /** The gear the work was PRICED at — not always the gear that was asked for. */
  gear: gearIdSchema,
  /** Whether the conditioning piece had already begun when it was taken. */
  metconStarted: z.boolean(),
  /** The minutes the athlete was told the session would gain. */
  addsMinutes: z.number(),
  addedAt: z.iso.datetime(),
});

export type SessionAdditionDTO = z.infer<typeof sessionAdditionSchema>;

/**
 * ONE CHANGE THE ATHLETE KEPT, AS PROVENANCE — published on the assembled session for the
 * reason `sessionAdditionSchema` is: it is the only thing on the wire that distinguishes a line
 * the engine drew from a line the athlete chose, and a reader that cannot tell them apart cannot
 * answer "what was I given?" versus "what did I change?".
 *
 * 🔑 IT IS ALSO WHAT THE EDITOR'S `Prescribed … / Reset` ROW NEEDS. The row prints what the
 * engine originally gave, beside what the line now is, and only the service knows the first.
 */
export const sessionLineSwapSchema = z.object({
  line: z.number().int().min(0),
  movement: z.string().nullable(),
  kilos: z.number().nullable(),
  /**
   * WHAT THE ENGINE PRESCRIBED FOR THIS LINE BEFORE THE CHANGE — the movement id and the weight
   * of the line as DRAWN, so `Reset` can be worded ("Prescribed 24 kg") without the app keeping
   * its own copy of a session it is no longer being served.
   */
  prescribed: z.object({
    movement: z.string(),
    name: z.string(),
    kilos: z.number().nullable(),
  }),
});

export type SessionLineSwapDTO = z.infer<typeof sessionLineSwapSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE ONE THING THE SESSION SCREEN SAYS ABOUT A LIFT (BUD-396 · BUD-384 · BUD-389)

   ═══ 🚨 AT MOST ONE, AND THE SERVICE PICKS IT ═══

   Three issues each wanted the same slot above the blocks, and three strips stacking there is
   a wall of notices nobody reads — which defeats the point of every one of them. So the wire
   carries ONE notice or none, already chosen, and the phone renders what it is given.

   THE PRIORITY IS THE SERVICE'S AND NOT THE PHONE'S for the reason this codebase gives about
   every other projection it composes: a rule implemented on the client is a rule implemented
   once per client, and the second reading is always the one that is wrong. It is also the only
   half that can see the athlete's stored answers — a notice that has been DECLINED must not be
   offered again, and a phone cannot know that without being sent the decision it is meant to
   have forgotten about.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHICH OF THE THREE THIS IS. Each names its own source rather than a shared register:
 *
 *   retest       — the lift is owed a real measurement. The stall exit ramp after a second
 *                  stall at one weight, a completed block every set of which was easy, or the
 *                  working offset at one of its bounds. OFFERED, never forced.
 *   deload       — the stall rule has already taken the working weight down. Declinable, and a
 *                  decline puts it back.
 *   been-a-while — the lift has not been trained past the layoff threshold. One line, no
 *                  buttons: the engine has already capped the first session back at the last
 *                  weight actually completed, so there is nothing to choose.
 */
export const LIFT_NOTICE_KINDS = ["retest", "deload", "been-a-while"] as const;
export const liftNoticeKindSchema = z.enum(LIFT_NOTICE_KINDS);
export type LiftNoticeKind = (typeof LIFT_NOTICE_KINDS)[number];

/**
 * A NOTICE ABOUT ONE LIFT, ON THE SESSION ABOUT TO LOAD IT.
 *
 * A DISCRIMINATED UNION AND NOT ONE OBJECT WITH A NULLABLE WEIGHT, on the terms this file
 * already states for `sessionColumnSchema`: "a field that is merely optional reads as a
 * permission". Exactly one of the three carries a number, and it is the one whose whole message
 * is a number.
 *
 * `name` TRAVELS RATHER THAN BEING LOOKED UP. The engine publishes no catalog, so every other
 * printed movement name on this wire is carried the same way (`movementLineSchema.name`); a
 * phone that resolved a slug itself would hold a second, staler copy of the catalog.
 */
export const liftNoticeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("retest"),
    movement: z.string(),
    name: z.string(),
  }),
  z.object({
    kind: z.literal("deload"),
    movement: z.string(),
    name: z.string(),
    /** The lighter working weight now prescribed, in kilos — what declining would undo. */
    kilos: z.number().positive(),
  }),
  z.object({
    kind: z.literal("been-a-while"),
    movement: z.string(),
    name: z.string(),
  }),
]);

export type LiftNoticeDTO = z.infer<typeof liftNoticeSchema>;

/**
 * A STARTED SESSION AS IT NOW STANDS — the drawn metcon, plus whatever has been added to it.
 *
 * ═══ WHY THIS SHAPE EXISTS RATHER THAN THE CLIENT MERGING ═══
 *
 * The alternative was serving the additions on their own and letting the phone splice them
 * into the redraw it already has. That would put the BLOCK ORDER rule (warmup · ramp ·
 * strength · conditioning · accessory · cooldown, stable-sorted so an offer's prep lands after
 * the session's own warm-up), the requirement re-derivation and the estimate arithmetic on the
 * client — three engine rules restated in a second language, which is the drift this codebase
 * spends its lint rules refusing. The engine composes; the phone renders.
 *
 * ⚠️ `findings` ARE NOT RE-RUN ON READ, and cannot honestly be. The engine's own `verify`
 * reports rather than re-runs, for the reason it states: re-running needs the room, the athlete
 * and the catalog handed back in, and a caller who supplied a different one would get a
 * different verdict from the one the session shipped with. So what is published is the drawn
 * column's findings UNION each addition's stored findings — the latter being what the gates
 * said about the metcon WITH that block appended, recorded when it was taken. Deduplicated on
 * gate, severity and reason code, because an addition's verdict necessarily re-reports every
 * finding the base column already had.
 */
export const startedSessionShapeSchema = z.object({
  /** The `session_logs` row this session is. */
  id: z.uuid(),
  title: z.string(),
  /** The draw it came from. Both non-null here: a session with neither cannot be read back. */
  seed: z.number().int().nonnegative(),
  gear: gearIdSchema,
  requestId: z.string(),
  shape: z.string().nullable(),
  format: formatFactsSchema.nullable(),
  /** In block order, never reordered. The drawn blocks and the added ones, merged. */
  blocks: z.array(sessionBlockSchema),
  /** Re-derived over the merged blocks — the checklist has to include what was added. */
  requirements: z.array(requirementGroupSchema),
  /** `totalMinutes` re-derived over the merged blocks; `workMinutes` is the metcon alone. */
  estimate: sessionEstimateSchema,
  machine: z.string().nullable(),
  findings: z.array(findingSchema),
  relaxations: z.array(relaxationIdSchema),
  /** Empty for a session nothing has been added to. */
  additions: z.array(sessionAdditionSchema),
  /**
   * THE MOVEMENT THE SESSION IS ABOUT — the lift the strength log opens on.
   *
   * ⚠️ ADDED IN BUD-347 BECAUSE THIS SHAPE BECAME THE APP'S ONLY READ OF A STARTED SESSION.
   * The session screen used to fetch `GET /v1/sessions/suggestions/:seed`, which publishes it
   * on `suggestionSchema.lift`; it now reads THIS, because a session carrying a swap is not the
   * session that seed draws. The field travelled with the old read and had to travel with the
   * new one, or the strength log would go back to finding the lift by position — the same
   * defect the deck card had.
   */
  lift: z.string().nullable(),
  /**
   * DID THE ATHLETE CHANGE A LINE OF IT (BUD-347) — `session_logs.modified`, published because
   * frame `6a` promised it: "it will not compare to your benchmark". Whatever says that on a
   * history screen reads this.
   */
  modified: z.boolean(),
  /**
   * EVERY LINE THEY CHANGED, in line order. Empty for a session read back exactly as drawn.
   *
   * ⚠️ IT CAN BE SHORTER THAN WHAT IS STORED. A swap is re-validated against a redraw dated
   * TODAY, so one that a restriction added this morning now refuses is dropped and the line
   * reads as prescribed — `api/src/engine/assembly/swaps.ts` argues why dropping is right here
   * and replaying is right for an added block. `modified` does NOT drop with it.
   */
  swaps: z.array(sessionLineSwapSchema),
  /**
   * THE ONE NOTICE ABOVE THE BLOCKS, OR NULL (BUD-396 · BUD-384 · BUD-389). See
   * `liftNoticeSchema`: at most one, chosen here so three issues cannot stack three strips
   * into a wall, and null is the ordinary session.
   */
  notice: liftNoticeSchema.nullable(),
});

export type StartedSessionShapeDTO = z.infer<typeof startedSessionShapeSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   WHAT ELSE COULD GO ON THIS LINE (BUD-336)

   The athlete taps one movement of the metcon and asks what else could stand there. The reps
   and the place in the round are held FIXED and the load is re-solved — so the answer is a
   list of lines, each one the piece as it would actually print with that movement in it.

   ═══ WHY IT HANGS OFF THE SEED AND CARRIES A LINE INDEX ═══

   The seed IS the session, exactly as it is for `GET /suggestions/:seed/work`: nothing is
   stored when an offer is drawn, so the server redraws and reads the slot the line filled off
   the shape. That is the ONLY way the slot is recoverable — `movementLineSchema` below carries
   the movement, the modality, the body and the equipment, and none of the slot's own demands
   (its modality, its wanted pattern, its volume, whether it insists on reps). A substitution
   that could not see those would be a modality filter, which is exactly the unsafe rule the
   coaching review refused.

   ═══ WHAT IS DELIBERATELY NOT HERE ═══

   APPLYING one. Answering costs nothing and writes nothing; a session whose line was actually
   swapped is no longer the session the seed draws, so it has to be written down somewhere
   durable — and `session_logs` holds a seed and a gear rather than blocks. That is a schema
   change and it is not in this PR: the swap the athlete keeps, and the `modified` flag that
   stops a benchmark being compared against a differently-scaled one, need a column.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHICH LINE OF WHICH COLUMN. `line` is the line's INDEX in the conditioning piece, which is
 * also its slot's index in the shape — they are index-aligned by construction, and that is
 * what the server relies on to recover what the slot demanded.
 *
 * `gear` IS REQUIRED AND HAS NO DEFAULT, for `workOffersQuerySchema`'s reason: the load is
 * re-solved at the column the athlete actually took, and defaulting it would answer a
 * substitute weighed for an intensity they did not choose.
 */
export const equivalentsQuerySchema = z.object({
  seed: z.coerce.number().int().min(0),
  gear: gearIdSchema,
  line: z.coerce.number().int().min(0),
});

export type EquivalentsQuery = z.infer<typeof equivalentsQuerySchema>;

/**
 * ONE SUBSTITUTE — the line the piece would print, and why it is a good answer.
 *
 * IT IS A WHOLE `movementLineSchema` AND NOT A NAME AND A WEIGHT. The load is re-solved, the
 * break-up point is re-computed against this athlete's capacity for THIS movement, and the
 * expected seconds are this movement's — so a card that showed only a name and a number would
 * be showing the original line with a different word on it.
 */
export const equivalentSchema = z.object({
  line: movementLineSchema,
  /**
   * EVERY WEIGHT THIS ROOM CAN BUILD FOR THIS MOVEMENT, ascending — see `loads` on the answer
   * below. Carried per option because taking a substitute changes which rungs exist: a room's
   * kettlebells and its barbell are different ladders.
   */
  loads: z.array(z.number()),
  reasons: z.array(reasonSchema),
});

export type EquivalentDTO = z.infer<typeof equivalentSchema>;

/**
 * THE LIST, AND THE LINE IT IS ABOUT.
 *
 * AN EMPTY LIST IS A RESULT AND NOT AN ERROR, exactly as a refused column is. Holding the reps
 * fixed while the work time, the load class and the rung all stay in band is a narrow ask, and
 * a small room legitimately has nothing that meets it; `reasons` is what says so.
 *
 * `of` IS NULL only when there is no such line to be about — an index past the end of the
 * piece, a gear that refused, or a request too short to draw at all.
 */
export const equivalentsSchema = z.object({
  gear: gearIdSchema,
  of: movementLineSchema.nullable(),
  /**
   * ═══ EVERY WEIGHT THE ATHLETE MAY CHOOSE FOR `of`, ASCENDING (BUD-347) ═══
   *
   * 🚨 THE STEPPER ASKS RATHER THAN STEPPING BY AN INVENTED NUMBER. The line editor's
   * `−  24 kg  +` has to move by SOMETHING, and the only honest something is the next weight
   * the room can actually make: 2.5 kg offers 26.5 kg in a gym whose bells go 24 → 28, and
   * 22.5 kg in one whose smallest plate is 5. The client moves an index along this list and
   * computes no arithmetic of its own.
   *
   * WHAT BOUNDS IT: what the room owns, never above the athlete's own one-rep max for that
   * movement, and a limit-load restriction applied on top. `api/src/engine/assembly/loads.ts`
   * owns all three; a client that filtered this list would be a second implementation of them.
   *
   * 🔑 IT IS ALSO WHY THE DESIGN'S THIRD ROW IS NOT AN OPTION. Frame `6a` draws "keep the
   * movement, drop the load" beside two real substitutes, and it is not one — it changes the
   * load, which the substitution path holds SOLVED rather than chosen. The ladder is how that
   * ask is answered, by the editor's own load control.
   *
   * EMPTY IS A RESULT: an air squat has nothing to load, and a room owning nothing that carries
   * this movement has no rung to offer. The editor draws no stepper.
   */
  loads: z.array(z.number()),
  options: z.array(equivalentSchema),
  reasons: z.array(reasonSchema),
});

export type EquivalentsDTO = z.infer<typeof equivalentsSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   KEEPING ONE (BUD-347) — the write BUD-336 deliberately did not build

   BUD-336's own header said why applying was out of scope: "a session whose line was actually
   swapped is no longer the session the seed draws, so it has to be written down somewhere
   durable — and `session_logs` holds a seed and a gear rather than blocks. That is a schema
   change and it is not in this PR." `db/schemas/tables/session_line_swaps.sql` is the column,
   and this is the wire.

   ═══ 🚨 IT SENDS IDENTIFIERS, EXACTLY AS `addSessionBlockSchema` DOES ═══

   There is no field for a line, for a load the client computed, or for a session. The service
   re-draws at the session's own seed, RE-COMPOSES the substitutes for that line, and looks for
   the movement id among them; the weight has to be one of the rungs the equivalents answer
   published. So the property BUD-337 wrote into `session_blocks.sql` survives this table too:
   nothing a client sends becomes work an athlete is asked to do.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * CHANGING ONE LINE, OR PUTTING IT BACK.
 *
 * ═══ ALL THREE STATES ARE THIS ONE BODY, AND THAT IS DELIBERATE ═══
 *
 *   a movement and no weight — the equivalents screen's answer, at whatever the load re-solved to
 *   a weight and no movement — the editor's own stepper, on the movement that was drawn
 *   neither                  — the design's `Reset`. It REMOVES the change; the line goes back to
 *                              what the engine prescribed and `session_logs.modified` falls with
 *                              it, because a session that is once again exactly what was drawn
 *                              compares to a benchmark honestly.
 *
 * A separate DELETE action for the third would need the line index in the path and would give
 * the editor two endpoints for one button. One action, one round trip, and the table's own
 * check (`movement is not null or kilos is not null`) is what makes "neither" mean removal
 * rather than a row that changes nothing.
 *
 * ⚠️ THERE IS NO `reps` FIELD AND THERE NEVER WILL BE. Reps are never editable, anywhere: the
 * format owns the count, and the substitution path refuses any candidate whose rebuild moves
 * ANY line's count or total (`repsHeld`). There is no locked-reps state on the wire because
 * there is no editable one.
 *
 * ⚠️ THE GEAR IS NOT HERE EITHER, unlike `addSessionBlockSchema`. The session's own row carries
 * the column the athlete committed to, and the load is re-solved at that column — so taking it
 * from the client would let a swap be priced at an intensity they did not start.
 *
 * ⚠️ THE ID IS IN THE PATH AND NOT IN THIS SHAPE. `domain()` parses `input` from the BODY ALONE
 * on a POST, so a path param never reaches a handler through it; the route parses the id with
 * `startedSessionIdSchema`, which is what the app validates against too.
 */
export const swapSessionLineSchema = z.object({
  /** The line's index in the conditioning piece — the same index the equivalents query takes. */
  line: z.number().int().min(0),
  /**
   * The substitute's movement id, as `GET /v1/sessions/suggestions/:seed/equivalents` answered
   * it on `options[].line.movement`. Null keeps the movement that was drawn.
   */
  movement: z.string().min(1).max(200).nullable().default(null),
  /**
   * The weight, in kg, and it must be one of the rungs that answer published on `loads`. Null
   * takes the prescribed one.
   *
   * ⚠️ COMPARED FOR EQUALITY AGAINST THE LADDER, so it travels as the number that was published
   * and not as one the client rounded. 22.5 is a real rung; 22.499999999999996 is refused.
   */
  kilos: z.number().positive().max(9999).nullable().default(null),
});

export type SwapSessionLinePayload = z.infer<typeof swapSessionLineSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE FULLER LIST OF LIFTS, BEHIND `Browse` ON THE ADD-WORK SHEET (BUD-338)

   `GET /v1/sessions/suggestions/:seed/work` above answers THREE curated rows. This is the
   escape hatch behind them — design frame `5a`, "Pick a lift" — and it is deliberately the
   path that BYPASSES that curation: pattern tabs (`All` preselected, then Hinge / Squat /
   Push / Pull), intent tabs (Heavy / Volume), and every row arriving with a ready scheme,
   its loads and a truthful `+min`.

   ═══ 🚨 WHY IT IS A SECOND ACTION AND NOT A WIDER `?limit=` ON THE SHEET ═══

   They are two different questions and the coaching rules on them DIFFER, which is the
   whole reason this schema exists rather than a parameter. The sheet is choosing FOR the
   athlete, so it refuses every lift that touches what the metcon is about to work. This
   list is the athlete choosing, so — on a coaching review taken before it was built — it
   refuses only a repeated movement PATTERN (a hazard no member can see: nobody knows that a
   swing and a deadlift are the same movement) and merely WARNS about shared tissue (a metcon
   that will feel harder, which is entirely their call). A parameter on one action would have
   made those two rule sets one function with a flag, and the flag would eventually be wrong.

   It is also ENUMERATED rather than drawn: the sheet's three rows come off the generator,
   and a list the athlete moves through with two sets of tabs may not, or the rows they were
   reading reshuffle when they change a filter.

   ═══ WHAT IS DELIBERATELY NOT HERE ═══

   THE ACCESSORY SLOT HAS NO BROWSE, and the design draws none. An accessory offer is a set
   of drills sized to a number of minutes rather than a movement anybody picks off a list —
   `+6 min` and `+9 min` of the same idea — so a "fuller list" of them is a list of one thing
   at more sizes, which the sheet already is.

   ADDING one, exactly as on the sheet: this action ANSWERS rows and writes nothing.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * THE PATTERNS A ROW MAY BE FILTERED BY — the design's four tabs and the six the engine also
 * knows, published as one closed set.
 *
 * 🚨 DECLARED HERE AND ASSERTED EQUAL TO THE ENGINE'S, the same treatment `REASON_CODES` gets
 * and for the same reason: the app may not import the engine and the engine may not import
 * `shared/`, so a list both halves need is declared twice and checked in both directions —
 * `api/tests/engine/engine-contract.int.spec.ts`. The alternative was `pattern: z.string()`,
 * under which a mis-typed tab is not a 422 but a silently empty screen.
 *
 * A MODALITY TAG IS NOT A PATTERN. `gymnastic` and `monostructural` are family names thirty
 * catalog rows share; the engine's `functionalPatterns` drops them before any rule counts one,
 * and they are absent here for the same reason a tab named "gymnastics" would be useless.
 *
 * THE CLIENT SHOWS FOUR OF THESE, WHICH IS ITS CHOICE AND NOT THIS LIST'S. Frame `5a` draws
 * Hinge, Squat, Push (`press`) and Pull because those are the four a lifter thinks in. The
 * wire stays open to the rest so a tab can be added without a contract change.
 */
export const BROWSE_PATTERNS = [
  "press",
  "pull",
  "overhead",
  "squat",
  "hinge",
  "posterior-chain",
  "olympic",
  "core",
  "knee-flexion",
  "unilateral",
] as const;

export const browsePatternSchema = z.enum(BROWSE_PATTERNS);
export type BrowsePattern = (typeof BROWSE_PATTERNS)[number];

/**
 * THE TWO INTENT TABS. `paired` is NOT one: a superset is not an intensity, it is a way of
 * spending the rest a set earns, so pairing is a property of a row (`group`) rather than a
 * third tab. Every paired row is a volume row.
 */
export const BROWSE_INTENTS = ["heavy", "volume"] as const;
export const browseIntentSchema = z.enum(BROWSE_INTENTS);
export type BrowseIntent = (typeof BROWSE_INTENTS)[number];

/**
 * WHAT THE CALLER ASKS FOR.
 *
 * `gear` and `metconStarted` READ EXACTLY AS THEY DO ON THE SHEET, deliberately: the same
 * three-way pricing rule runs behind both, and `metconStarted` still only ever LOCKS — priced
 * at the easy column, single lifts only, and the practice row gone. Not `z.coerce.boolean()`,
 * which is a trap on a query string (`?metconStarted=false` arrives as the non-empty string
 * "false" and coerces to TRUE).
 *
 * `pattern` AND `intent` ARE OMITTED RATHER THAN SENT AS A WORD FOR "ALL". The design's `All`
 * tab is the absence of a filter, and an `"all"` sentinel would be a second value meaning the
 * same thing as no value — which is how a client ends up sending both and a server ends up
 * deciding which wins. Absent means unfiltered, and the answer echoes what it was given.
 */
export const browseLiftsQuerySchema = z.object({
  seed: z.coerce.number().int().min(0),
  gear: gearIdSchema,
  metconStarted: z
    .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
    .default(false),
  pattern: browsePatternSchema.optional(),
  intent: browseIntentSchema.optional(),
});

export type BrowseLiftsQuery = z.infer<typeof browseLiftsQuerySchema>;

/**
 * WHICH OF THE THREE TIERS A ROW IS IN — and this is the field a reviewer should look at,
 * because THE ORDER IS THE COACHING RATHER THAN THE BADGE.
 *
 * A general-population member taps the first row that looks good, so the first row has to be
 * the right one. That makes the sort a coaching decision, which is why the engine both does it
 * and says what it did: the rows arrive already ordered `rested`, then `rehearsal`, then
 * `caution`, and this field is what lets a client draw the divider frame `5a` puts above the
 * warned rows without deciding for itself which rows belong under it.
 *
 *   rested     works nothing today's metcon works. The natural answer.
 *   rehearsal  the metcon's OWN movement, offered as practice at the same weight — the frame's
 *              `In metcon` badge. Before the metcon only; once the clock has run it is gone.
 *   caution    shares tissue with the metcon: a loaded joint while fresh, or any body part
 *              afterwards. Shown, badged and last. Never greyed out — grey reads as disabled,
 *              and a member who taps a disabled row concludes the app is broken.
 *
 * 🚨 A TIER IS NEVER A REFUSAL. Everything refused is absent from `lifts` entirely, with its
 * code on `BrowseLiftsDTO.reasons` — so a row that is here is a row the athlete may take.
 */
export const BROWSE_TIERS = ["rested", "rehearsal", "caution"] as const;
export const browseTierSchema = z.enum(BROWSE_TIERS);
export type BrowseTier = (typeof BROWSE_TIERS)[number];

/** The design's own three row shapes, and its own two badges for the last two. */
export const BROWSE_GROUPS = ["single", "superset", "giant-set"] as const;
export const browseGroupSchema = z.enum(BROWSE_GROUPS);
export type BrowseGroup = (typeof BROWSE_GROUPS)[number];

/**
 * ONE ROW — everything frame `5a` draws, and everything the session would gain by taking it.
 *
 * `blocks` IS WHOLE `sessionBlockSchema`, the same trade `workOfferSchema` makes and for the
 * same reason: these are the blocks that get APPENDED the moment somebody taps one, so a
 * lighter second shape here would be a second reading of a movement line to drift. What keeps
 * that affordable is `browseRowsMax` — see `reasons`, which says when the list was cut.
 */
export const browseLiftSchema = z.object({
  /**
   * WHICH ROW THIS IS, stable for a given (seed, gear, clock) — the movement ids of the work
   * itself, joined, prefixed `browse:`. NOT a minted id: nothing is stored, so there is no row
   * to name. A single and the superset built on the same first lift are different ids because
   * they are different work.
   */
  id: z.string(),
  /**
   * THE MOVEMENT THE ROW IS ABOUT — the first lift, which is what the primary button names
   * ("Add deadlift"). A group's limbs are on `blocks`, each line carrying its own `pairing`
   * label, and composing "Deadlift + row" is the client's sentence to write. The engine holds
   * no sentences.
   */
  lift: z.string(),
  group: browseGroupSchema,
  intent: browseIntentSchema,
  tier: browseTierSchema,
  /**
   * THE ROW'S OWN FUNCTIONAL PATTERNS — which tab it answers to, sent rather than inferred. A
   * client cannot derive them: that would need the movement vocabulary, which is exactly what
   * the engine does not publish. Judged on the lift the row is ABOUT, never on a group's mates:
   * a mate is chosen for NOT competing with the first lift, so counting its patterns would put
   * a squat row under the `Pull` tab.
   */
  patterns: z.array(z.string()),
  /**
   * 🚨 WHAT THE SESSION GAINS IN MINUTES — the number frame `5a` prints as `+22 min`, and the
   * one field here that is a promise rather than a description. It is the sum of every block the
   * row would add: the extra warm-up the athlete's own warm-up did not cover, THE BAR'S CLIMB,
   * and the working sets with their rest. A `+min` that left the ramp out would be a lie the
   * athlete pays for by skipping the climb to save the minutes the row promised them.
   */
  addsMinutes: z.number().int().nonnegative(),
  /**
   * 🚨 THE WORK ITSELF, AND NOT THE THREE WHOLE BLOCKS `workOfferSchema` CARRIES — BUD-305's
   * lesson applied one screen over, and the one place this payload deliberately diverges from
   * the sheet's.
   *
   * MEASURED rather than assumed: a thirty-five-row list in the `box` preset carrying whole
   * blocks weighed 101 KB, of which the prep and the ramp were 42% and each `MovementLine`'s
   * `equipment`, `space`, `body`, `standard`, `breakInto` and `total` most of the remainder —
   * while frame `5a` draws a scheme, two or three lines with a load, and one rest sentence. The
   * SHEET may carry three whole blocks because there are three of them; a list of thirty-five
   * cannot, and BUD-305 is this repo's own record of a screen that tried (a page of ten whole
   * suggestions at 180,832 bytes, cut to cards).
   *
   * THE PREP AND THE RAMP ARE COUNTED, ITEMISED AND NOT SHIPPED. They are inside `addsMinutes`
   * and broken out on `spends`, so the promise stays auditable from the row — which was the
   * property that mattered rather than the blocks. Nothing on this screen renders them: the
   * sheet is where an offer is inspected, and a chosen row is REDRAWN rather than carried,
   * exactly as `POST /v1/sessions/start` redraws rather than trusting a body.
   *
   * `rest` IS PRESENT HERE AND ABSENT FROM `deckBlockSchema`, and that is not an oversight of
   * the deck's: "No rest between A1 and A2 · 2:00 after" IS the difference between a superset
   * and two lifts listed one under the other, and it is the sentence frame `5a` prints under a
   * bracket. A metcon's rest lives in its format, which is why a card never needed the field.
   */
  work: z.object({
    prescription: prescriptionSchema,
    rest: restRuleSchema,
    /** The same trimmed line the deck card carries — `A1  Deadlift · 4 × 4    115 kg`. */
    lines: z.array(deckLineSchema),
  }),
  /**
   * WHERE THE `+min` WENT — three numbers instead of three blocks, and what keeps the promise
   * checkable after the trim: "22 min — 2 warming up, 4 climbing, 16 lifting" without the drills
   * having to travel.
   *
   * ⚠️ THE THREE NEED NOT SUM TO `addsMinutes`. Each is rounded from its own seconds, while
   * `addsMinutes` is rounded ONCE from the total — which is the only way it can agree with the
   * length gates that measured it. Rounding three parts and adding them is how an estimate
   * drifts a minute away from the thing it was checked as.
   */
  spends: z.object({
    prepMinutes: z.number().int().nonnegative(),
    rampMinutes: z.number().int().nonnegative(),
    workMinutes: z.number().int().nonnegative(),
  }),
  /** What this row would add to the session's equipment checklist. */
  requirements: z.array(requirementGroupSchema),
  /**
   * WHY THIS ROW IS WHAT IT IS — codes with values, never a composed sentence.
   *
   * IT CARRIES THE WORK BLOCK'S OWN REASONS TOO, hoisted rather than nested under a block this
   * payload no longer has. They are what explain the numbers the row PRINTS — "working sets at
   * this gear", "heavy working weight", "a giant set is shorter and lighter" — so dropping them
   * with the block would have left a scheme and a load traceable to nothing.
   */
  reasons: z.array(reasonSchema),
  /**
   * 🚨 WHAT THE ATHLETE SHOULD KNOW BEFORE TAPPING — separate from `reasons` on purpose.
   *
   * `reasons` says why a row is good; `cautions` says what it costs, and a client renders the
   * two differently: a badge and a line under the divider rather than a supporting sentence.
   * Flattened into one array the screen would have to decide which of its own codes were
   * warnings, which is a client re-deciding a coaching question.
   *
   * THE PRIMARY `Add` BUTTON ON A CAUTIONED ROW SHOULD CARRY THE CAUTION'S OWN WORDING, and
   * there is deliberately no second confirmation for it: a confirm dialogue on a caution teaches
   * people to dismiss confirms, and then the one place a confirm would matter is already dead.
   */
  cautions: z.array(reasonSchema),
  /**
   * WHAT THE GATES SAID ABOUT THE METCON *WITH THIS APPENDED* — not about the row alone, and
   * exactly as on the sheet. A row a gate REJECTED is not in the list at all, with the gate's
   * own reason on the list's `reasons` instead. So an entry here is a warning about a session
   * the athlete may still choose, never a refusal they are being shown anyway.
   */
  findings: z.array(findingSchema),
});

export type BrowseLiftDTO = z.infer<typeof browseLiftSchema>;

/**
 * THE LIST — the rows in coaching order, and why it is what it is.
 *
 * `reasons` IS LIST-SCOPE and is where every refusal lands: the pattern repeats that were
 * hidden and how many, the week's heavy days being spent, a gate ceiling that refused rows, the
 * list being cut to a page, and the two shapes of empty. AN EMPTY LIST IS A RESULT AND NOT AN
 * ERROR — and no tab may ever come back blank with nothing said, because an empty tab is a
 * retap and a retap is how somebody chooses worse.
 *
 * 🚨 `gear` IS WHAT THE ROWS WERE PRICED AT, WHICH IS NOT ALWAYS WHAT WAS ASKED FOR. Two rules
 * move it and they are the two safety rules that exist to move it: rows appended once the clock
 * has run are priced at the easy column, and a week whose heavy days are spent is priced under
 * the heavy floor. This is also why THE HEAVY TAB NEVER COMES BACK EMPTY on a spent week — it
 * comes back with rows priced under the floor and a code saying why, because refusing a lift to
 * somebody who tapped in order to lift buys nothing and costs the screen its credibility.
 */
export const browseLiftsSchema = z.object({
  gear: gearIdSchema,
  /** The tabs this answer was filtered by, echoed. Null is the design's preselected `All`. */
  pattern: browsePatternSchema.nullable(),
  intent: browseIntentSchema.nullable(),
  lifts: z.array(browseLiftSchema),
  reasons: z.array(reasonSchema),
});

export type BrowseLiftsDTO = z.infer<typeof browseLiftsSchema>;
